import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Typography,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  Slider,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SettingsIcon from '@mui/icons-material/Settings';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { DndContext, DragEndEvent, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS, apiConfig, resolveAssetUrl } from '../utils/apiConfig';
import {
  TEMPERATURE_SENSOR_MODELS,
  calculateSensorResistance,
  calculateSensorTemperature,
  formatResistanceValue,
  getSensorModelById,
  getSensorResistanceRange
} from '../utils/temperatureSensors';
import dokumenteService, { DokumentEntry } from '../services/dokumenteService';
import telefonlisteService, { TelefonlisteEntry } from '../services/telefonlisteService';
import { tokens } from '../theme/tokens';
import CarRaceGame from '../features/car-race/CarRaceGame';

type UpdateItem = {
  id: number;
  title: string;
  body: string;
  is_active: number | boolean;
  created_at?: string;
};

type DashboardData = {
  globalNote: string;
  userNote: string;
  widgetOrder: string[];
  widgetEnabled: Record<string, boolean>;
  widgetSizes: Record<string, WidgetSize>;
  widgetGap?: number;
};

type HighscoreEntry = {
  id: string;
  alias: string;
  rank: number;
  totalPoints: number;
};

type MyScore = {
  alias: string;
  totalPoints: number;
  rank: number | null;
};

type WidgetSize = { w: number; h: number };

type WidgetConfig = {
  key: string;
  title: string;
  category: string;
  description?: string;
  route?: string;
  type?: 'profile' | 'updates' | 'highscore' | 'globalNote' | 'userNote' | 'faq' | 'docs' | 'index' | 'unit' | 'quick';
  infoText?: string;
};

const DEFAULT_WIDGET_ORDER: string[] = [];
const DEFAULT_WIDGET_ENABLED: Record<string, boolean> = {};
const DEFAULT_WIDGET_SIZES: Record<string, WidgetSize> = {};

const GRID_COLUMNS = 24;
const GRID_ROW_HEIGHT = 80;
const DEFAULT_WIDGET_GAP = 24;
const MIN_WIDGET_W = 4;
const MIN_WIDGET_H = 1;
const MAX_WIDGET_W = GRID_COLUMNS;
const MAX_WIDGET_H = 8;

const DEFAULT_WIDGET_SIZE: WidgetSize = { w: 8, h: 2 };

const DEFAULT_WIDGET_SIZE_BY_KEY: Record<string, WidgetSize> = {
  profile: { w: 8, h: 3 },
  updates: { w: 12, h: 3 },
  highscore: { w: 8, h: 3 },
  globalNote: { w: 12, h: 3 },
  userNote: { w: 12, h: 3 },
  index: { w: 12, h: 2 },
  m98_u1_f_a_q: { w: 8, h: 2 },
  m98_u2_vorschlaege: { w: 12, h: 4 },
  m98_u3_highscore: { w: 8, h: 3 },
  m98_u4_telefonliste: { w: 12, h: 3 },
  m98_u5_dokumente: { w: 8, h: 2 },
  m99_u1_dipschalter: { w: 12, h: 3 },
  m99_u2_taupunkt: { w: 8, h: 3 },
  m99_u3_volumenstrom: { w: 8, h: 3 },
  m99_u4_temperaturen: { w: 10, h: 3 },
  m99_u5_kabelberechnung: { w: 12, h: 3 },
  m99_u6_leistungsberechnung: { w: 12, h: 3 },
  m99_u7_widerstand: { w: 8, h: 3 },
  m99_u8_skalierer: { w: 12, h: 4 },
  m99_u9_farbcodex: { w: 12, h: 4 },
  m99_u10_energiekostenrechner: { w: 12, h: 4 },
  m99_u11_energierechner: { w: 12, h: 4 },
  quick_units: { w: 10, h: 2 },
  quick_ohm: { w: 8, h: 2 },
  quick_pressure: { w: 8, h: 2 },
  quick_power: { w: 12, h: 3 },
  quick_volume: { w: 10, h: 2 },
  quick_taupunkt: { w: 10, h: 3 },
  quick_contact: { w: 10, h: 3 },
  quick_air_density: { w: 10, h: 2 },
  quick_pipe_loss: { w: 10, h: 2 },
  quick_energy_cost: { w: 10, h: 2 },
  quick_fan_laws: { w: 12, h: 3 }
};

const clampWidgetSize = (size: WidgetSize): WidgetSize => ({
  w: Math.min(MAX_WIDGET_W, Math.max(MIN_WIDGET_W, Math.round(size.w))),
  h: Math.min(MAX_WIDGET_H, Math.max(MIN_WIDGET_H, Math.round(size.h)))
});

const normalizeWidgetSize = (value: unknown, key?: string): WidgetSize => {
  if (!value) return DEFAULT_WIDGET_SIZE_BY_KEY[key ?? ''] ?? DEFAULT_WIDGET_SIZE;
  if (typeof value === 'string') {
    switch (value) {
      case 'sm':
        return { w: 6, h: 2 };
      case 'md':
        return { w: 8, h: 2 };
      case 'lg':
        return { w: 12, h: 3 };
      default:
        return DEFAULT_WIDGET_SIZE_BY_KEY[key ?? ''] ?? DEFAULT_WIDGET_SIZE;
    }
  }
  if (typeof value === 'object') {
    const asAny = value as { w?: number; h?: number };
    if (Number.isFinite(asAny.w) && Number.isFinite(asAny.h)) {
      return clampWidgetSize({ w: Number(asAny.w), h: Number(asAny.h) });
    }
  }
  return DEFAULT_WIDGET_SIZE_BY_KEY[key ?? ''] ?? DEFAULT_WIDGET_SIZE;
};

const BASE_WIDGETS: WidgetConfig[] = [
  { key: 'profile', title: 'Mein Profil', category: 'Basis', type: 'profile' },
  { key: 'updates', title: 'Letzte Updates', category: 'Info', type: 'updates' },
  { key: 'highscore', title: 'Highscore (Top 5)', category: 'Info', type: 'highscore' },
  { key: 'globalNote', title: 'Notiz (Global)', category: 'Notizen', type: 'globalNote' },
  { key: 'userNote', title: 'Notiz (Mein)', category: 'Notizen', type: 'userNote' },
  { key: 'index', title: 'Inhaltsverzeichnis', category: 'Navigation', type: 'index' }
];

const M98_WIDGETS: WidgetConfig[] = [
  { key: 'm98_u1_f_a_q', title: 'FAQ', category: 'M98', type: 'faq', route: '/units/m98_u1_f_a_q', description: 'Kurzuebersicht der letzten Fragen.' },
  { key: 'm98_u2_vorschlaege', title: 'Vorschlaege', category: 'M98', type: 'unit', route: '/units/m98_u2_vorschlaege', description: 'Feedback und Bilder senden.' },
  { key: 'm98_u3_highscore', title: 'Highscore', category: 'M98', type: 'highscore', route: '/units/m98_u3_highscore', description: 'Top 5 und eigener Stand.' },
  { key: 'm98_u4_telefonliste', title: 'Telefonliste', category: 'M98', type: 'unit', route: '/units/m98_u4_telefonliste', description: 'Kontakte und Ansprechpersonen.' },
  { key: 'm98_u5_dokumente', title: 'Dokumente', category: 'M98', type: 'docs', route: '/units/m98_u5_dokumente', description: 'Letzte Dokumente und Downloads.' }
];

const M99_WIDGETS: WidgetConfig[] = [
  { key: 'm99_u1_dipschalter', title: 'DIP-Schalter', category: 'M99', type: 'unit', route: '/units/m99_u1_dipschalter', description: 'Kurzinfo und Schnellstart.' },
  { key: 'm99_u2_taupunkt', title: 'Taupunkt', category: 'M99', type: 'unit', route: '/units/m99_u2_taupunkt', description: 'Berechnungen im Kurzformat.' },
  { key: 'm99_u3_volumenstrom', title: 'Volumenstrom', category: 'M99', type: 'unit', route: '/units/m99_u3_volumenstrom', description: 'Schnelle Rechenhilfe.' },
  { key: 'm99_u4_temperaturen', title: 'Temperaturen', category: 'M99', type: 'unit', route: '/units/m99_u4_temperaturen', description: 'Widerstand oder Temperatur passiver Fuehler berechnen.' },
  { key: 'm99_u5_kabelberechnung', title: 'Kabelberechnung', category: 'M99', type: 'unit', route: '/units/m99_u5_kabelberechnung', description: 'Leitungsauswahl im Kurzformat.' },
  { key: 'm99_u6_leistungsberechnung', title: 'Leistungsberechnung', category: 'M99', type: 'unit', route: '/units/m99_u6_leistungsberechnung', description: 'Kurz-Tool fuer Leistung.' },
  { key: 'm99_u7_widerstand', title: 'Widerstand', category: 'M99', type: 'unit', route: '/units/m99_u7_widerstand', description: 'Schnellrechner fuer Widerstand.' },
  { key: 'm99_u8_skalierer', title: 'Skalierer', category: 'M99', type: 'unit', route: '/units/m99_u8_skalierer', description: 'Linear vor/zurueck inkl. Rechenweg.' },
  { key: 'm99_u9_farbcodex', title: 'Farb-Codex', category: 'M99', type: 'unit', route: '/units/m99_u9_farbcodex', description: 'Paint-Picker + HEX/RGB/HSL/RAL.' },
  { key: 'm99_u10_energiekostenrechner', title: 'Energie-Kosten', category: 'M99', type: 'unit', route: '/units/m99_u10_energiekostenrechner', description: 'Strom/Waerme bidirektional.' },
  { key: 'm99_u11_energierechner', title: 'Raum Energie', category: 'M99', type: 'unit', route: '/units/m99_u11_energierechner', description: 'Heizen/Kuehlen mit Rechenweg.' }
];

const QUICK_WIDGETS: WidgetConfig[] = [
  { 
    key: 'quick_units', 
    title: 'Einheiten-Umrechner', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Schnell Umrechnen von Standardwerten.',
    infoText: 'Rechnet häufig benötigte Einheiten um: Leistung (W/kW), Temperatur (°C/K) und Volumenstrom (m³/h / l/s). Geben Sie einen Wert ein und wählen Sie die Einheit – die Umrechnung erfolgt sofort.'
  },
  { 
    key: 'quick_ohm', 
    title: 'Ohm-Gesetz', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'U, I oder R blitzschnell berechnen.',
    infoText: 'Berechnet das Ohm-Gesetz (U = R × I). Wählen Sie die gesuchte Größe (U, I oder R) und geben Sie die beiden anderen Werte ein. Das Tool berechnet automatisch die fehlende Größe.'
  },
  { 
    key: 'quick_pressure', 
    title: 'Druck-Umrechner', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Pa, kPa, mbar sofort umrechnen.',
    infoText: 'Rechnet Druckwerte zwischen Pascal (Pa), Kilopascal (kPa) und Millibar (mbar) um. Geben Sie einen Wert mit Einheit ein – die Umrechnung in alle drei Einheiten erfolgt automatisch.'
  },
  { 
    key: 'quick_power', 
    title: 'Leistung AC/DC Kurz', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'P, S, Q, cos φ und sin φ.',
    infoText: 'Berechnet elektrische Leistung für DC, AC1 (Einphasig) oder AC3 (Drehstrom). Eingabe: Spannung (U), Strom (I) und optional cos φ. Ausgabe: Wirkleistung (P), Scheinleistung (S), Blindleistung (Q) und sin φ.'
  },
  { 
    key: 'quick_volume', 
    title: 'Volumenstrom +', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Rund/Rechteck-Kanal, sofort Q.',
    infoText: 'Berechnet den Volumenstrom Q (m³/h) aus Kanalgeometrie und Geschwindigkeit. Wählen Sie runden oder rechteckigen Kanal, geben Sie die Abmessungen (Durchmesser oder Breite/Höhe in mm) und die Geschwindigkeit (m/s) ein. Optional: k-Faktor (0.8-0.95, typisch) als Korrekturfaktor für nicht-ideale Strömungen. Formel: Q = k × v × A × 3600.'
  },
  { 
    key: 'quick_taupunkt', 
    title: 'Taupunkt Quick-Chart', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Kurzberechnung mit Mini-Chart.',
    infoText: 'Berechnet den Taupunkt aus Temperatur (°C) und relativer Feuchte (%). Der Taupunkt ist die Temperatur, bei der die Luftfeuchtigkeit kondensiert. Wichtig zur Vermeidung von Kondenswasser an Oberflächen.'
  },
  { 
    key: 'quick_contact', 
    title: 'Kontakt-Suche', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Telefonliste sofort durchsuchen.',
    infoText: 'Durchsucht die Telefonliste nach Namen, Email oder Telefonnummer. Geben Sie einen Suchbegriff ein und die Ergebnisse werden in Echtzeit gefiltert.'
  },
  { 
    key: 'quick_air_density', 
    title: 'Luftdichte', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'ρ aus T, p, rF.',
    infoText: 'Berechnet die Luftdichte ρ (kg/m³) aus Temperatur (°C), Luftdruck (Pa) und relativer Feuchte (%). Die Luftdichte ist wichtig für Berechnungen von Volumenstrom, Druckverlust und Ventilatorleistung.'
  },
  { 
    key: 'quick_pipe_loss', 
    title: 'Rohrverlust', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Δp pro m aus v, D.',
    infoText: 'Berechnet den Druckverlust Δp (Pa/m) in geraden Rohrleitungen aus Geschwindigkeit v (m/s), Durchmesser D (mm), Reibungsbeiwert λ und Dichte ρ (kg/m³). Formel: Δp = λ × (ρ × v² / 2) × (1 / D).'
  },
  { 
    key: 'quick_energy_cost', 
    title: 'Energie-Kosten', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'kWh & Preis -> Kosten.',
    infoText: 'Berechnet die Energiekosten aus Leistung (kW), Betriebsstunden und Strompreis (€/kWh). Hilfreich für Wirtschaftlichkeitsberechnungen und Betriebskostenabschätzungen.'
  },
  { 
    key: 'quick_fan_laws', 
    title: 'Ventilator-Gesetze', 
    category: 'Quick Tools', 
    type: 'quick', 
    description: 'Q, Δp, P bei Drehzahl.',
    infoText: 'Berechnet die Änderung von Volumenstrom (Q), Druckdifferenz (Δp) und Leistung (P) eines Ventilators bei Drehzahländerung. Gesetze: Q ~ n, Δp ~ n², P ~ n³. Eingabe: Q1, Δp1, P1, n1 (alte Drehzahl), n2 (neue Drehzahl).'
  }
];

const DIP_VALUES = [1, 2, 4, 8, 16, 32, 64, 128];
const DEFAULT_CONTACT_PLACEHOLDER = '/assets/drawable/ic_contact_placeholder.png';
const numberFormatter = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const normalizeNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseHex = (value: string): { r: number; g: number; b: number } | null => {
  const trimmed = value.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(trimmed)) return null;
  const full = trimmed.length === 3
    ? trimmed.split('').map((c) => c + c).join('')
    : trimmed;
  const num = Number.parseInt(full, 16);
  return {
    r: (num >> 16) & 0xff,
    g: (num >> 8) & 0xff,
    b: num & 0xff
  };
};

const sinPhiFromCos = (cosPhi: number): number => {
  const clamped = Math.max(0, Math.min(1, cosPhi));
  return Math.sqrt(1 - clamped * clamped);
};

const DipSwitchSVG: React.FC<{ index: number; isOn: boolean; onToggle?: () => void }> = ({ index, isOn, onToggle }) => {
  const switchColor = isOn ? '#1B873B' : '#B2140F';
  return (
    <svg width="70" height="80" viewBox="0 0 90 100" style={{ cursor: onToggle ? 'pointer' : 'default' }} onClick={onToggle}>
      <rect x="5" y="5" width="80" height="90" rx="6" fill="#0f172a" />
      <rect x="20" y="22" width="50" height="56" rx="4" fill="#111" stroke="#111" strokeWidth="1" />
      <rect x="32" y={isOn ? 24 : 46} width="26" height="26" rx="3" fill={switchColor} stroke="#000" strokeWidth="1" />
      <text x="45" y="88" fontSize="12" fill="#fff" fontWeight="bold" textAnchor="middle">{index + 1}</text>
    </svg>
  );
};

const isFormFieldTarget = (target: EventTarget | null): boolean => {
  if (!target || typeof window === 'undefined') return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
};

type SortableWidgetCardProps = {
  widget: WidgetConfig;
  dragDisabled: boolean;
  widgetSizes: Record<string, WidgetSize>;
  widgetGap: number;
  isSmallScreen: boolean;
  onRemove: (key: string) => void;
  onResizeStart: (key: string, size: WidgetSize, event: React.PointerEvent<HTMLDivElement>) => void;
  renderWidgetContent: (widget: WidgetConfig) => React.ReactNode;
  setDragDisabled: (value: boolean) => void;
};

const SortableWidgetCard: React.FC<SortableWidgetCardProps> = React.memo(({
  widget,
  dragDisabled,
  widgetSizes,
  widgetGap,
  isSmallScreen,
  onRemove,
  onResizeStart,
  renderWidgetContent,
  setDragDisabled
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.key,
    disabled: dragDisabled
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1
  };
  const size = widgetSizes[widget.key] || normalizeWidgetSize(undefined, widget.key);
  const spanW = isSmallScreen ? 12 : Math.max(MIN_WIDGET_W, Math.min(size.w, GRID_COLUMNS));
  const spanH = Math.max(size.h, MIN_WIDGET_H);
  const minHeight = spanH * GRID_ROW_HEIGHT + Math.max(0, (spanH - 1) * widgetGap);
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ gridColumn: `span ${spanW}`, minWidth: 0, minHeight }}
    >
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 2,
          height: '100%',
          position: 'relative',
          border: '1px solid rgba(13,53,89,0.18)',
          boxShadow: '0 8px 18px rgba(13,53,89,0.08)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton size="small" {...attributes} {...listeners}>
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
            {widget.title}
          </Typography>
          {widget.infoText && (
            <Tooltip title={widget.infoText} placement="top" arrow>
              <IconButton size="small" sx={{ color: 'rgba(13,53,89,0.6)' }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => onRemove(widget.key)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box
          sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', pr: 0.5, pt: 0.5 }}
          onFocusCapture={(event) => {
            if (isFormFieldTarget(event.target)) setDragDisabled(true);
          }}
          onBlurCapture={() => {
            setTimeout(() => {
              if (typeof document === 'undefined') return;
              if (!isFormFieldTarget(document.activeElement)) {
                setDragDisabled(false);
              }
            }, 0);
          }}
        >
          {renderWidgetContent(widget)}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 10,
            fontSize: 11,
            color: 'rgba(13,53,89,0.6)'
          }}
        >
          {spanW} × {spanH}
        </Box>
        {!isSmallScreen && (
          <Box
            onPointerDown={(event) => onResizeStart(widget.key, size, event)}
            sx={{
              position: 'absolute',
              right: 6,
              bottom: 6,
              width: 18,
              height: 18,
              borderRadius: 1,
              border: '1px solid rgba(13,53,89,0.35)',
              background: 'linear-gradient(135deg, rgba(13,53,89,0.15), rgba(178,20,15,0.2))',
              cursor: 'nwse-resize'
            }}
          />
        )}
      </Paper>
    </Box>
  );
});

const DashboardDropZone: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'dashboard-drop' });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        border: isOver ? '2px dashed #B2140F' : '2px dashed transparent',
        borderRadius: 2,
        transition: 'border-color 0.2s ease',
        p: 1,
        width: '100%',
        maxWidth: 'calc(100vw - 32px)',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {children}
    </Box>
  );
};

const MODULE_LINKS = [
  { id: 'M1', label: 'M1 Lueftung' },
  { id: 'M2', label: 'M2 Waermeerzeugung' },
  { id: 'M3', label: 'M3 Kaelte' },
  { id: 'M4', label: 'M4 Regelung' },
  { id: 'M5', label: 'M5 Bus Systeme' },
  { id: 'M6', label: 'M6 Geraete' },
  { id: 'M10', label: 'M10 Funktionen' },
  { id: 'M50', label: 'M50 Step-by-Step' },
  { id: 'M98', label: 'M98 Hilfe/FAQ' },
  { id: 'M99', label: 'M99 Tools/Highscore' }
];

const Start: React.FC = () => {
  const {
    token,
    user,
    refreshProfile,
    canManageDashboard,
    canManageUpdates,
    canManageUsers,
    canManageTeams,
    canManageQuiz,
    canViewDocuments,
    canManagePhonebook,
    canViewPhonebook,
    canManageHighscore,
    canViewRolesLegend,
    canEditRolesLegend,
    canAccessAdmin,
    canManageThemeSettings,
    canPlayCarRace
  } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const canAccessAdminPanel = canAccessAdmin || canManageUpdates || canEditRolesLegend || canManageThemeSettings;

  const [message, setMessage] = useState<string | null>(null);
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [raceOpen, setRaceOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    globalNote: '',
    userNote: '',
    widgetOrder: DEFAULT_WIDGET_ORDER,
    widgetEnabled: DEFAULT_WIDGET_ENABLED,
    widgetSizes: DEFAULT_WIDGET_SIZES,
    widgetGap: DEFAULT_WIDGET_GAP
  });
  const [localGlobalNote, setLocalGlobalNote] = useState('');
  const [localUserNote, setLocalUserNote] = useState('');
  const [widgetOrder, setWidgetOrder] = useState<string[]>(DEFAULT_WIDGET_ORDER);
  const [widgetEnabled, setWidgetEnabled] = useState<Record<string, boolean>>(DEFAULT_WIDGET_ENABLED);
  const [widgetSizes, setWidgetSizes] = useState<Record<string, WidgetSize>>(DEFAULT_WIDGET_SIZES);
  const [widgetGap, setWidgetGap] = useState<number>(DEFAULT_WIDGET_GAP);
  const [widgetsDirty, setWidgetsDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingWidgets, setSavingWidgets] = useState(false);
  const [topEntries, setTopEntries] = useState<HighscoreEntry[]>([]);
  const [myScore, setMyScore] = useState<MyScore | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [documents, setDocuments] = useState<DokumentEntry[]>([]);
  const [faqEntries, setFaqEntries] = useState<Array<{ id: number; question: string }>>([]);
  const [contacts, setContacts] = useState<TelefonlisteEntry[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsQuery, setContactsQuery] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [profileImageMessage, setProfileImageMessage] = useState<string | null>(null);
  const profileImageInputRef = useRef<HTMLInputElement | null>(null);
  const [suggestionFile, setSuggestionFile] = useState<File | null>(null);
  const [suggestionPreview, setSuggestionPreview] = useState<string | null>(null);
  const [suggestionComment, setSuggestionComment] = useState('');
  const [suggestionMessage, setSuggestionMessage] = useState<string | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [suggestionSending, setSuggestionSending] = useState(false);
  const suggestionFileRef = useRef<HTMLInputElement | null>(null);
  const [dipAddressInput, setDipAddressInput] = useState('');
  const [dipStates, setDipStates] = useState<boolean[]>([false, false, false, false, false, false, false, false]);
  const [dipError, setDipError] = useState<string | null>(null);
  const [tauTemp, setTauTemp] = useState('');
  const [tauRh, setTauRh] = useState('');
  const [tempSensorType, setTempSensorType] = useState(TEMPERATURE_SENSOR_MODELS[0]?.id || '');
  const [tempSensorDirection, setTempSensorDirection] = useState<'temp_to_res' | 'res_to_temp'>('temp_to_res');
  const [tempSensorValue, setTempSensorValue] = useState('20');
  const [tempSensorResistanceValue, setTempSensorResistanceValue] = useState('100');
  const [volSpeed, setVolSpeed] = useState('');
  const [volDiameter, setVolDiameter] = useState('');
  const [cableSystem, setCableSystem] = useState<'DC' | 'AC1' | 'AC3'>('AC3');
  const [cablePower, setCablePower] = useState('');
  const [cableVoltage, setCableVoltage] = useState('400');
  const [cableCosPhi, setCableCosPhi] = useState('0.9');
  const [powerSystem, setPowerSystem] = useState<'DC' | 'AC1' | 'AC3'>('AC1');
  const [powerVoltage, setPowerVoltage] = useState('230');
  const [powerCurrent, setPowerCurrent] = useState('');
  const [powerCosPhi, setPowerCosPhi] = useState('0.95');
  const [resVoltage, setResVoltage] = useState('');
  const [resCurrent, setResCurrent] = useState('');
  const [scaleX1, setScaleX1] = useState('0');
  const [scaleX2, setScaleX2] = useState('10');
  const [scaleY1, setScaleY1] = useState('0');
  const [scaleY2, setScaleY2] = useState('100');
  const [scaleInput, setScaleInput] = useState('5');
  const [scaleDirection, setScaleDirection] = useState<'forward' | 'reverse'>('forward');
  const [scaleCurveModeWidget, setScaleCurveModeWidget] = useState<'linear' | 'parabola' | 'sine'>('linear');
  const [scaleSliderWidget, setScaleSliderWidget] = useState(50);
  const [scaleParAWidget, setScaleParAWidget] = useState('1');
  const [scaleParHWidget, setScaleParHWidget] = useState('5');
  const [scaleParKWidget, setScaleParKWidget] = useState('50');
  const [scaleSinAmpWidget, setScaleSinAmpWidget] = useState('30');
  const [scaleSinOffsetWidget, setScaleSinOffsetWidget] = useState('50');
  const [scaleSinPeriodWidget, setScaleSinPeriodWidget] = useState('10');
  const [scaleSinPhaseWidget, setScaleSinPhaseWidget] = useState('0');
  const [colorHex, setColorHex] = useState('#B2140F');
  const [colorR, setColorR] = useState('178');
  const [colorG, setColorG] = useState('20');
  const [colorB, setColorB] = useState('15');
  const [energyFlow, setEnergyFlow] = useState('2.0');
  const [energyDeltaT, setEnergyDeltaT] = useState('5');
  const [energyHoursM99, setEnergyHoursM99] = useState('8');
  const [energyPriceM99, setEnergyPriceM99] = useState('0.35');
  const [roomVolume, setRoomVolume] = useState('200');
  const [roomArea, setRoomArea] = useState('80');
  const [roomHeight, setRoomHeight] = useState('2.5');
  const [roomGeometryMode, setRoomGeometryMode] = useState<'volume' | 'area_height'>('area_height');
  const [roomDeltaT, setRoomDeltaT] = useState('10');
  const [roomHoursM99, setRoomHoursM99] = useState('4');
  const [roomWidgetMode, setRoomWidgetMode] = useState<'energy' | 'heating'>('energy');
  const [roomHeatDeltaT, setRoomHeatDeltaT] = useState('20');
  const [roomHeatU, setRoomHeatU] = useState('1.0');
  const [roomHeatArea, setRoomHeatArea] = useState('50');
  const [roomHeatN, setRoomHeatN] = useState('0.5');
  const [roomHeatVolume, setRoomHeatVolume] = useState('125');
  const [roomHeatReheat, setRoomHeatReheat] = useState('0');
  const [unitType, setUnitType] = useState<'power' | 'temperature' | 'flow'>('power');
  const [unitValue, setUnitValue] = useState('');
  const [unitInput, setUnitInput] = useState<'W' | 'kW' | 'C' | 'K' | 'm3h' | 'ls'>('kW');
  const [ohmTarget, setOhmTarget] = useState<'U' | 'I' | 'R'>('U');
  const [ohmU, setOhmU] = useState('');
  const [ohmI, setOhmI] = useState('');
  const [ohmR, setOhmR] = useState('');
  const [pressureValue, setPressureValue] = useState('');
  const [pressureUnit, setPressureUnit] = useState<'Pa' | 'kPa' | 'mbar'>('Pa');
  const [quickContactsQuery, setQuickContactsQuery] = useState('');
  const [volShape, setVolShape] = useState<'round' | 'rect'>('round');
  const [volWidth, setVolWidth] = useState('');
  const [volHeight, setVolHeight] = useState('');
  const [volKFactor, setVolKFactor] = useState('1.0');
  const [airTemp, setAirTemp] = useState('20');
  const [airPressure, setAirPressure] = useState('1013');
  const [airRh, setAirRh] = useState('50');
  const [pipeVelocity, setPipeVelocity] = useState('2');
  const [pipeDiameter, setPipeDiameter] = useState('200');
  const [pipeFriction, setPipeFriction] = useState('0.02');
  const [pipeDensity, setPipeDensity] = useState('1.2');
  const [energyPower, setEnergyPower] = useState('1.5');
  const [energyHours, setEnergyHours] = useState('8');
  const [energyPrice, setEnergyPrice] = useState('0.35');
  const [fanQ1, setFanQ1] = useState('1000');
  const [fanDp1, setFanDp1] = useState('200');
  const [fanP1, setFanP1] = useState('500');
  const [fanN1, setFanN1] = useState('1450');
  const [fanN2, setFanN2] = useState('1800');
  const [dragDisabled, setDragDisabled] = useState(false);
  const dashboardGridRef = useRef<HTMLDivElement | null>(null);
  const [resizing, setResizing] = useState<{
    key: string;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    colWidth: number;
  } | null>(null);

  const currentId = user?.id ? String(user.id) : null;
  const currentEmail = user?.email ?? null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [token, navigate]);

  const loadDashboardData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${apiConfig.apiUrl}?action=getDashboardData`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.data) {
        const incoming = data.data as DashboardData;
        const rawOrder = incoming.widgetOrder?.length ? incoming.widgetOrder : DEFAULT_WIDGET_ORDER;
        const nextOrder = [...rawOrder];
        const nextEnabled = { ...DEFAULT_WIDGET_ENABLED, ...(incoming.widgetEnabled || {}) };
        const rawSizes = incoming.widgetSizes || {};
        const rawGap = incoming.widgetGap;
        const nextGap = Number.isFinite(Number(rawGap)) ? Math.max(8, Math.min(48, Number(rawGap))) : DEFAULT_WIDGET_GAP;
        const nextSizes: Record<string, WidgetSize> = { ...DEFAULT_WIDGET_SIZES };
        Object.entries(rawSizes).forEach(([key, value]) => {
          nextSizes[key] = normalizeWidgetSize(value, key);
        });
        setDashboardData({
          globalNote: incoming.globalNote || '',
          userNote: incoming.userNote || '',
          widgetOrder: nextOrder,
          widgetEnabled: nextEnabled,
          widgetSizes: nextSizes,
          widgetGap: nextGap
        });
        setWidgetOrder(nextOrder);
        setWidgetEnabled(nextEnabled);
        setWidgetSizes(nextSizes);
        setWidgetGap(nextGap);
        setWidgetsDirty(false);
      }
    } catch {
      // ignore
    }
  }, [token]);

  const loadUpdates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${apiConfig.apiUrl}?action=getUpdates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && Array.isArray(data.updates)) {
        setUpdates(data.updates);
      }
    } catch {
      // ignore
    }
  }, [token]);

  const loadHighscores = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${apiConfig.apiUrl}?action=getAllUsers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !Array.isArray(data)) {
        return;
      }
      const ranked = data
        .filter((item: any) => item && item.deleted !== true && item.deleted !== 1)
        .map((item: any) => {
          let sum = 0;
          Object.keys(item).forEach((key) => {
            if (key.startsWith('quiz_')) {
              const value = typeof item[key] === 'string' ? parseInt(item[key], 10) : item[key];
              if (Number.isFinite(value)) sum += value;
            }
          });
          return {
            id: String(item.id ?? ''),
            email: String(item.email ?? ''),
            alias: item.alias ? String(item.alias) : 'Unbekannt',
            totalPoints: sum
          };
        })
        .filter((item: any) => item.id || item.email)
        .sort((a: any, b: any) => b.totalPoints - a.totalPoints);

      const rankByScore = new Map<number, number>();
      ranked.forEach((entry: any, index: number) => {
        if (!rankByScore.has(entry.totalPoints)) {
          rankByScore.set(entry.totalPoints, index + 1);
        }
      });

      const topFive = ranked.slice(0, 5).map((entry: any) => ({
        id: entry.id,
        alias: entry.alias,
        totalPoints: entry.totalPoints,
        rank: rankByScore.get(entry.totalPoints) ?? 0
      }));

      const currentUserScore = ranked.find((entry: any) => {
        if (currentId && entry.id === currentId) return true;
        if (currentEmail && entry.email === currentEmail) return true;
        return false;
      });

      setTopEntries(topFive);
      setMyScore(
        currentUserScore
          ? {
              alias: currentUserScore.alias,
              totalPoints: currentUserScore.totalPoints,
              rank: rankByScore.get(currentUserScore.totalPoints) ?? null
            }
          : null
      );
    } catch {
      // ignore
    }
  }, [currentEmail, currentId, token]);

  const loadDocuments = useCallback(async () => {
    if (!token || !canViewDocuments) return;
    try {
      const response = await dokumenteService.getDocuments(token);
      setDocuments(response.documents);
    } catch {
      // ignore
    }
  }, [token, canViewDocuments]);

  const loadFaq = useCallback(async () => {
    try {
      const response = await fetch(`${apiConfig.apiUrl}?action=getFaqEntries`, { method: 'GET' });
      const data = await response.json().catch(() => null);
      if (response.ok && Array.isArray(data)) {
        setFaqEntries(
          data.slice(0, 3).map((entry: any) => ({
            id: Number(entry?.id) || 0,
            question: String(entry?.question_text || '')
          }))
        );
      }
    } catch {
      // ignore
    }
  }, []);

  const loadContacts = useCallback(async () => {
    if (!token) return;
    setContactsLoading(true);
    try {
      const response = await telefonlisteService.fetch(token);
      if (Array.isArray(response.entries)) {
        setContacts(response.entries);
      }
    } catch {
      // ignore
    } finally {
      setContactsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDashboardData();
    loadUpdates();
  }, [loadDashboardData, loadUpdates]);

  // Synchronisiere lokale Note-States mit geladenen Daten
  useEffect(() => {
    setLocalGlobalNote(dashboardData.globalNote);
    setLocalUserNote(dashboardData.userNote);
  }, [dashboardData.globalNote, dashboardData.userNote]);

  useEffect(() => {
    if (!widgetOrder.length) return;
    if (widgetOrder.includes('highscore') || widgetOrder.includes('m98_u3_highscore')) {
      loadHighscores();
    }
    if (widgetOrder.includes('m98_u5_dokumente')) {
      loadDocuments();
    }
    if (widgetOrder.includes('m98_u1_f_a_q')) {
      loadFaq();
    }
    if (widgetOrder.includes('m98_u4_telefonliste')) {
      loadContacts();
    }
    if (widgetOrder.includes('quick_contact')) {
      loadContacts();
    }
  }, [widgetOrder, loadDocuments, loadFaq, loadHighscores, loadContacts]);

  useEffect(() => {
    setProfileImage(resolveAssetUrl(user?.profile_image ?? null));
  }, [user]);

  useEffect(() => {
    if (!suggestionFile) {
      setSuggestionPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(suggestionFile);
    setSuggestionPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [suggestionFile]);

  useEffect(() => {
    if (unitType === 'power') setUnitInput('kW');
    if (unitType === 'temperature') setUnitInput('C');
    if (unitType === 'flow') setUnitInput('m3h');
  }, [unitType]);

  const visibleUpdates = useMemo(() => updates.filter((u) => Boolean(u.is_active)), [updates]);

  const updatesBannerText = useMemo(() => {
    if (!visibleUpdates.length) return 'Keine Updates vorhanden.';
    return visibleUpdates
      .map((u) => `${u.title}: ${u.body}`)
      .join('   •   ');
  }, [visibleUpdates]);

  const handleSaveNotes = useCallback(async (payload: { globalNote?: string; userNote?: string }) => {
    if (!token) return;
    setSavingNotes(true);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'updateDashboardNotes', ...payload })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && data?.data) {
        setDashboardData((prev) => ({
          ...prev,
          globalNote: data.data.globalNote ?? prev.globalNote,
          userNote: data.data.userNote ?? prev.userNote
        }));
        setMessage('Notiz wurde gespeichert.');
      } else {
        setMessage(data?.error || 'Notiz konnte nicht gespeichert werden.');
      }
    } catch {
      setMessage('Notiz konnte nicht gespeichert werden.');
    } finally {
      setSavingNotes(false);
    }
  }, [token]);

  const handleSaveWidgets = useCallback(async () => {
    if (!token) return;
    setSavingWidgets(true);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateDashboardWidgets',
          widgetOrder,
          widgetEnabled,
          widgetSizes,
          widgetGap
        })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setWidgetsDirty(false);
        setMessage('Dashboard-Layout gespeichert.');
      } else {
        setMessage(data?.error || 'Dashboard-Layout konnte nicht gespeichert werden.');
      }
    } catch {
      setMessage('Dashboard-Layout konnte nicht gespeichert werden.');
    } finally {
      setSavingWidgets(false);
    }
  }, [token, widgetEnabled, widgetOrder, widgetSizes, widgetGap]);

  const handleAddWidget = useCallback((key: string, insertBefore?: string) => {
    setWidgetOrder((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev];
      if (insertBefore && next.includes(insertBefore)) {
        next.splice(next.indexOf(insertBefore), 0, key);
      } else {
        next.push(key);
      }
      return next;
    });
    setWidgetEnabled((prev) => ({ ...prev, [key]: true }));
    setWidgetSizes((prev) => ({ ...prev, [key]: prev[key] || normalizeWidgetSize(undefined, key) }));
    setWidgetsDirty(true);
  }, []);

  const handleRemoveWidget = useCallback((key: string) => {
    setWidgetOrder((prev) => prev.filter((item) => item !== key));
    setWidgetEnabled((prev) => ({ ...prev, [key]: false }));
    setWidgetsDirty(true);
  }, []);

  const adjustWidgetSize = useCallback((key: string, deltaW: number, deltaH: number) => {
    setWidgetSizes((prev) => {
      const current = prev[key] || normalizeWidgetSize(undefined, key);
      const next = clampWidgetSize({ w: current.w + deltaW, h: current.h + deltaH });
      return { ...prev, [key]: next };
    });
    setWidgetsDirty(true);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId.startsWith('available:')) {
      const key = activeId.replace('available:', '');
      if (overId === 'dashboard-drop') {
        handleAddWidget(key);
      } else if (widgetOrder.includes(overId)) {
        handleAddWidget(key, overId);
      }
      return;
    }

    if (widgetOrder.includes(activeId) && widgetOrder.includes(overId) && activeId !== overId) {
      setWidgetOrder((prev) => arrayMove(prev, prev.indexOf(activeId), prev.indexOf(overId)));
      setWidgetsDirty(true);
    }
  }, [handleAddWidget, widgetOrder]);

  const handleProfileImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setProfileImageFile(file);
    setProfileImageMessage(null);
  };

  const handleProfileImageUpload = async () => {
    if (!token) {
      setProfileImageMessage('Nicht authentifiziert');
      return;
    }
    if (!profileImageFile) {
      setProfileImageMessage('Bitte zuerst ein Bild auswaehlen.');
      return;
    }
    const formData = new FormData();
    formData.append('action', 'uploadProfileImage');
    formData.append('image_file', profileImageFile);
    setProfileImageUploading(true);
    try {
      const response = await fetch(API_ENDPOINTS.api, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Upload fehlgeschlagen');
      }
      setProfileImage(resolveAssetUrl(data.image));
      setProfileImageFile(null);
      if (profileImageInputRef.current) profileImageInputRef.current.value = '';
      setProfileImageMessage('Profilbild gespeichert.');
      await refreshProfile();
    } catch (e: any) {
      setProfileImageMessage(e?.message || 'Upload fehlgeschlagen');
    } finally {
      setProfileImageUploading(false);
    }
  };

  const handleSuggestionFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSuggestionFile(file);
    setSuggestionMessage(null);
    setSuggestionError(null);
  };

  const handleSuggestionUpload = async () => {
    setSuggestionMessage(null);
    setSuggestionError(null);
    if (!token) {
      setSuggestionError('Bitte zuerst einloggen, um einen Vorschlag zu senden.');
      return;
    }
    if (!suggestionFile) {
      setSuggestionError('Bitte zuerst ein Bild auswählen.');
      return;
    }
    const formData = new FormData();
    formData.append('action', 'uploadUserImageSimple');
    formData.append('comment', suggestionComment.trim());
    formData.append('image_file', suggestionFile);
    setSuggestionSending(true);
    try {
      const response = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        throw new Error(data?.error || `Serverfehler: ${response.status}`);
      }
      if (data.success) {
        setSuggestionMessage(data.message || 'Upload erfolgreich.');
        setSuggestionFile(null);
        setSuggestionComment('');
        if (suggestionFileRef.current) {
          suggestionFileRef.current.value = '';
        }
      } else {
        throw new Error(data.error || 'Upload fehlgeschlagen.');
      }
    } catch (err: any) {
      setSuggestionError(err?.message || 'Upload fehlgeschlagen.');
    } finally {
      setSuggestionSending(false);
    }
  };

  const handleDipToggle = (index: number) => {
    setDipStates((prev) => prev.map((value, i) => (i === index ? !value : value)));
  };

  const handleApplyDipAddress = () => {
    const raw = dipAddressInput.trim();
    if (!raw) {
      setDipError('Adresse fehlt.');
      return;
    }
    if (!/^[0-9]+$/.test(raw)) {
      setDipError('Nur Ziffern zulässig.');
      return;
    }
    const address = Number(raw);
    if (address < 1 || address > 255) {
      setDipError('Adresse muss zwischen 1 und 255 liegen.');
      return;
    }
    const states = DIP_VALUES.map(() => false);
    let remaining = address;
    for (let index = DIP_VALUES.length - 1; index >= 0; index -= 1) {
      const value = DIP_VALUES[index];
      if (remaining >= value) {
        states[index] = true;
        remaining -= value;
      }
    }
    setDipStates(states);
    setDipError(null);
  };

  const dipAddressValue = useMemo(
    () => dipStates.reduce((sum, isOn, index) => (isOn ? sum + DIP_VALUES[index] : sum), 0),
    [dipStates]
  );

  const dewPointResult = useMemo(() => {
    const tempValue = normalizeNumber(tauTemp);
    const rhValue = normalizeNumber(tauRh);
    if (tempValue === null || rhValue === null) return '';
    if (rhValue < 0 || rhValue > 100) return 'Feuchtigkeit 0-100%';
    const a = 17.62;
    const b = 243.12;
    const alpha = (a * tempValue) / (b + tempValue) + Math.log(rhValue / 100);
    const dewPoint = (b * alpha) / (a - alpha);
    if (!Number.isFinite(dewPoint)) return 'Berechnung nicht möglich.';
    return `Taupunkt: ${numberFormatter.format(dewPoint)} °C`;
  }, [tauTemp, tauRh]);

  const selectedTempSensorModel = useMemo(() => getSensorModelById(tempSensorType), [tempSensorType]);

  const tempSensorResistanceRange = useMemo(
    () => (selectedTempSensorModel ? getSensorResistanceRange(selectedTempSensorModel.id) : undefined),
    [selectedTempSensorModel]
  );

  const temperatureSensorQuickResult = useMemo(() => {
    if (!tempSensorType) return '';
    const model = getSensorModelById(tempSensorType);
    if (!model) return 'Fuehlertyp nicht gefunden.';

    if (tempSensorDirection === 'temp_to_res') {
      const tempValue = normalizeNumber(tempSensorValue);
      if (tempValue === null) return '';
      const result = calculateSensorResistance(tempSensorType, tempValue);
      if (!result.ok) return result.error;
      return `${model.label}: ${formatResistanceValue(result.resistanceOhm)}`;
    }

    const resistanceValue = normalizeNumber(tempSensorResistanceValue);
    if (resistanceValue === null) return '';
    const result = calculateSensorTemperature(tempSensorType, resistanceValue);
    if (!result.ok) return result.error;
    return `${model.label}: ${numberFormatter.format(result.temperatureC)} °C`;
  }, [tempSensorType, tempSensorDirection, tempSensorValue, tempSensorResistanceValue]);

  const volumeFlowResult = useMemo(() => {
    const speed = normalizeNumber(volSpeed);
    const diameterMm = normalizeNumber(volDiameter);
    const widthMm = normalizeNumber(volWidth);
    const heightMm = normalizeNumber(volHeight);
    const kFactor = normalizeNumber(volKFactor) ?? 1.0;
    if (speed === null) return '';
    if (volShape === 'round' && diameterMm === null) return '';
    if (volShape === 'rect' && (widthMm === null || heightMm === null)) return '';
    if (speed <= 0) return 'Werte muessen > 0 sein.';
    let area = 0;
    if (volShape === 'round') {
      const diameterM = (diameterMm ?? 0) / 1000;
      if (diameterM <= 0) return 'Werte muessen > 0 sein.';
      area = Math.PI * (diameterM * diameterM) / 4;
    } else {
      const widthM = (widthMm ?? 0) / 1000;
      const heightM = (heightMm ?? 0) / 1000;
      if (widthM <= 0 || heightM <= 0) return 'Werte muessen > 0 sein.';
      area = widthM * heightM;
    }
    const flow = kFactor * speed * area * 3600;
    return `Q = ${numberFormatter.format(flow)} m³/h`;
  }, [volDiameter, volSpeed, volWidth, volHeight, volShape, volKFactor]);

  const unitConversionResult = useMemo(() => {
    const value = normalizeNumber(unitValue);
    if (value === null) return [] as string[];
    if (unitType === 'power') {
      const watts = unitInput === 'kW' ? value * 1000 : value;
      return [`${numberFormatter.format(watts)} W`, `${numberFormatter.format(watts / 1000)} kW`];
    }
    if (unitType === 'temperature') {
      const celsius = unitInput === 'K' ? value - 273.15 : value;
      const kelvin = unitInput === 'C' ? value + 273.15 : value;
      return [`${numberFormatter.format(celsius)} °C`, `${numberFormatter.format(kelvin)} K`];
    }
    const m3h = unitInput === 'ls' ? value * 3.6 : value;
    const ls = unitInput === 'm3h' ? value / 3.6 : value;
    return [`${numberFormatter.format(m3h)} m³/h`, `${numberFormatter.format(ls)} l/s`];
  }, [unitInput, unitType, unitValue]);

  const ohmResult = useMemo(() => {
    const u = normalizeNumber(ohmU);
    const i = normalizeNumber(ohmI);
    const r = normalizeNumber(ohmR);
    if (ohmTarget === 'U') {
      if (i === null || r === null) return '';
      return `U = ${numberFormatter.format(i * r)} V`;
    }
    if (ohmTarget === 'I') {
      if (u === null || r === null || r === 0) return '';
      return `I = ${numberFormatter.format(u / r)} A`;
    }
    if (u === null || i === null || i === 0) return '';
    return `R = ${numberFormatter.format(u / i)} Ω`;
  }, [ohmI, ohmR, ohmTarget, ohmU]);

  const pressureResult = useMemo(() => {
    const value = normalizeNumber(pressureValue);
    if (value === null) return [] as string[];
    let pa = value;
    if (pressureUnit === 'kPa') pa = value * 1000;
    if (pressureUnit === 'mbar') pa = value * 100;
    return [
      `${numberFormatter.format(pa)} Pa`,
      `${numberFormatter.format(pa / 1000)} kPa`,
      `${numberFormatter.format(pa / 100)} mbar`
    ];
  }, [pressureUnit, pressureValue]);

  const quickPowerResult = useMemo(() => {
    const u = normalizeNumber(powerVoltage);
    const i = normalizeNumber(powerCurrent);
    if (u === null || i === null) return '';
    const cosPhi = powerSystem === 'DC' ? 1 : (normalizeNumber(powerCosPhi) ?? 1);
    const sinPhi = powerSystem === 'DC' ? 0 : sinPhiFromCos(cosPhi);
    let apparent = 0;
    if (powerSystem === 'AC3') {
      apparent = Math.sqrt(3) * u * i;
    } else {
      apparent = u * i;
    }
    const power = apparent * cosPhi;
    const reactive = apparent * sinPhi;
    return `P=${numberFormatter.format(power)} W · S=${numberFormatter.format(apparent)} VA · Q=${numberFormatter.format(reactive)} var · sin φ=${numberFormatter.format(sinPhi)}`;
  }, [powerCosPhi, powerCurrent, powerSystem, powerVoltage]);

  const dewPointValue = useMemo(() => {
    const tempValue = normalizeNumber(tauTemp);
    const rhValue = normalizeNumber(tauRh);
    if (tempValue === null || rhValue === null) return null;
    if (rhValue < 0 || rhValue > 100) return null;
    const a = 17.62;
    const b = 243.12;
    const alpha = (a * tempValue) / (b + tempValue) + Math.log(rhValue / 100);
    const dewPoint = (b * alpha) / (a - alpha);
    return Number.isFinite(dewPoint) ? dewPoint : null;
  }, [tauTemp, tauRh]);

  const airDensityResult = useMemo(() => {
    const temp = normalizeNumber(airTemp);
    const pressure = normalizeNumber(airPressure);
    const rhValue = normalizeNumber(airRh);
    if (temp === null || pressure === null || rhValue === null) return '';
    if (rhValue < 0 || rhValue > 100) return 'rF 0-100%';
    const tempK = temp + 273.15;
    const es = 6.112 * Math.exp((17.62 * temp) / (243.12 + temp));
    const e = (rhValue / 100) * es;
    const rho = (pressure * 100) / (287.05 * tempK) * (1 - 0.378 * (e / pressure));
    return `ρ ≈ ${numberFormatter.format(rho)} kg/m³`;
  }, [airPressure, airRh, airTemp]);

  const pipeLossResult = useMemo(() => {
    const v = normalizeNumber(pipeVelocity);
    const dMm = normalizeNumber(pipeDiameter);
    const f = normalizeNumber(pipeFriction);
    const rho = normalizeNumber(pipeDensity);
    if (v === null || dMm === null || f === null || rho === null) return '';
    if (v <= 0 || dMm <= 0 || f <= 0 || rho <= 0) return 'Werte muessen > 0 sein.';
    const d = dMm / 1000;
    const dp = f * (rho * v * v / 2) * (1 / d);
    return `Δp ≈ ${numberFormatter.format(dp)} Pa/m`;
  }, [pipeDiameter, pipeDensity, pipeFriction, pipeVelocity]);

  const energyCostResult = useMemo(() => {
    const power = normalizeNumber(energyPower);
    const hours = normalizeNumber(energyHours);
    const price = normalizeNumber(energyPrice);
    if (power === null || hours === null || price === null) return '';
    if (power < 0 || hours < 0 || price < 0) return 'Werte muessen >= 0 sein.';
    const kwh = power * hours;
    const cost = kwh * price;
    return `${numberFormatter.format(kwh)} kWh · ${numberFormatter.format(cost)} €`;
  }, [energyHours, energyPower, energyPrice]);

  const fanLawsResult = useMemo(() => {
    const q1 = normalizeNumber(fanQ1);
    const dp1 = normalizeNumber(fanDp1);
    const p1 = normalizeNumber(fanP1);
    const n1 = normalizeNumber(fanN1);
    const n2 = normalizeNumber(fanN2);
    if (q1 === null || dp1 === null || p1 === null || n1 === null || n2 === null) return '';
    if (n1 <= 0 || n2 <= 0) return 'Drehzahl > 0';
    const ratio = n2 / n1;
    const q2 = q1 * ratio;
    const dp2 = dp1 * ratio * ratio;
    const p2 = p1 * ratio * ratio * ratio;
    return `Q2=${numberFormatter.format(q2)} m³/h · Δp2=${numberFormatter.format(dp2)} Pa · P2=${numberFormatter.format(p2)} W`;
  }, [fanDp1, fanN1, fanN2, fanP1, fanQ1]);

  const cableQuickResult = useMemo(() => {
    const power = normalizeNumber(cablePower);
    const voltage = normalizeNumber(cableVoltage);
    if (power === null || voltage === null) return '';
    const cosPhi = normalizeNumber(cableCosPhi) ?? 1;
    if (voltage <= 0) return 'Spannung > 0';
    const powerW = power * 1000;
    let current = 0;
    if (cableSystem === 'DC') {
      current = powerW / voltage;
    } else if (cableSystem === 'AC1') {
      current = powerW / (voltage * cosPhi);
    } else {
      current = powerW / (Math.sqrt(3) * voltage * cosPhi);
    }
    if (!Number.isFinite(current)) return 'Berechnung nicht möglich.';
    const table = [
      { max: 16, section: '1.5' },
      { max: 25, section: '2.5' },
      { max: 32, section: '4' },
      { max: 40, section: '6' },
      { max: 63, section: '10' },
      { max: 80, section: '16' },
      { max: 100, section: '25' },
      { max: 125, section: '35' },
      { max: 160, section: '50' }
    ];
    const match = table.find((entry) => current <= entry.max);
    const section = match ? `${match.section} mm²` : '≥ 70 mm²';
    return `I ≈ ${numberFormatter.format(current)} A · Richtwert: ${section}`;
  }, [cableCosPhi, cablePower, cableSystem, cableVoltage]);

  const powerQuickResult = useMemo(() => {
    const voltage = normalizeNumber(powerVoltage);
    const current = normalizeNumber(powerCurrent);
    if (voltage === null || current === null) return '';
    const cosPhi = normalizeNumber(powerCosPhi) ?? 1;
    let power = 0;
    if (powerSystem === 'DC') {
      power = voltage * current;
    } else if (powerSystem === 'AC1') {
      power = voltage * current * cosPhi;
    } else {
      power = Math.sqrt(3) * voltage * current * cosPhi;
    }
    if (!Number.isFinite(power)) return 'Berechnung nicht möglich.';
    return `P ≈ ${numberFormatter.format(power)} W`;
  }, [powerCosPhi, powerCurrent, powerSystem, powerVoltage]);

  const resistanceQuickResult = useMemo(() => {
    const voltage = normalizeNumber(resVoltage);
    const current = normalizeNumber(resCurrent);
    if (voltage === null || current === null) return '';
    if (current === 0) return 'Strom darf nicht 0 sein.';
    const resistance = voltage / current;
    return `R = ${numberFormatter.format(resistance)} Ω`;
  }, [resCurrent, resVoltage]);

  const scaleResult = useMemo(() => {
    const x1 = normalizeNumber(scaleX1);
    const x2 = normalizeNumber(scaleX2);
    const y1 = normalizeNumber(scaleY1);
    const y2 = normalizeNumber(scaleY2);
    const sliderX = x1 !== null && x2 !== null ? Math.min(x1, x2) + ((Math.max(x1, x2) - Math.min(x1, x2)) * scaleSliderWidget) / 100 : null;
    const input = normalizeNumber(scaleInput) ?? sliderX;
    if (x1 === null || x2 === null || y1 === null || y2 === null || input === null) return '';
    if (scaleCurveModeWidget === 'linear' && scaleDirection === 'forward') {
      const denominator = x2 - x1;
      if (denominator === 0) return 'x1 und x2 duerfen nicht gleich sein.';
      const result = y1 + ((input - x1) * (y2 - y1)) / denominator;
      return `Y = ${numberFormatter.format(result)} (X→Y)`;
    }
    if (scaleCurveModeWidget === 'linear' && scaleDirection === 'reverse') {
      const denominator = y2 - y1;
      if (denominator === 0) return 'y1 und y2 duerfen nicht gleich sein.';
      const result = x1 + ((input - y1) * (x2 - x1)) / denominator;
      return `X = ${numberFormatter.format(result)} (Y→X)`;
    }
    if (scaleCurveModeWidget === 'parabola') {
      const a = normalizeNumber(scaleParAWidget);
      const h = normalizeNumber(scaleParHWidget);
      const k = normalizeNumber(scaleParKWidget);
      if (a === null || h === null || k === null) return 'Parabelparameter fehlen.';
      const result = a * (input - h) * (input - h) + k;
      return `Y = ${numberFormatter.format(result)} (Parabel)`;
    }
    const amp = normalizeNumber(scaleSinAmpWidget);
    const offset = normalizeNumber(scaleSinOffsetWidget);
    const period = normalizeNumber(scaleSinPeriodWidget);
    const phase = normalizeNumber(scaleSinPhaseWidget);
    if (amp === null || offset === null || period === null || phase === null || period === 0) return 'Sinusparameter fehlen.';
    const result = offset + amp * Math.sin((2 * Math.PI * (input - phase)) / period);
    return `Y = ${numberFormatter.format(result)} (Sinus)`;
  }, [
    scaleCurveModeWidget,
    scaleDirection,
    scaleInput,
    scaleParAWidget,
    scaleParHWidget,
    scaleParKWidget,
    scaleSinAmpWidget,
    scaleSinOffsetWidget,
    scaleSinPeriodWidget,
    scaleSinPhaseWidget,
    scaleSliderWidget,
    scaleX1,
    scaleX2,
    scaleY1,
    scaleY2
  ]);

  const colorCodexResult = useMemo(() => {
    const r = normalizeNumber(colorR);
    const g = normalizeNumber(colorG);
    const b = normalizeNumber(colorB);
    const fromHex = parseHex(colorHex);
    if (fromHex) {
      return `RGB(${fromHex.r}, ${fromHex.g}, ${fromHex.b})`;
    }
    if (r === null || g === null || b === null) return '';
    const rr = Math.max(0, Math.min(255, Math.round(r)));
    const gg = Math.max(0, Math.min(255, Math.round(g)));
    const bb = Math.max(0, Math.min(255, Math.round(b)));
    const hex = `#${[rr, gg, bb].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
    return `${hex} / RGB(${rr}, ${gg}, ${bb})`;
  }, [colorB, colorG, colorHex, colorR]);

  const colorPreviewHex = useMemo(() => {
    const fromHex = parseHex(colorHex);
    if (fromHex) {
      return colorHex;
    }
    const r = normalizeNumber(colorR);
    const g = normalizeNumber(colorG);
    const b = normalizeNumber(colorB);
    if (r === null || g === null || b === null) return '#B2140F';
    const rr = Math.max(0, Math.min(255, Math.round(r)));
    const gg = Math.max(0, Math.min(255, Math.round(g)));
    const bb = Math.max(0, Math.min(255, Math.round(b)));
    return `#${[rr, gg, bb].map((v) => v.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  }, [colorB, colorG, colorHex, colorR]);

  const energyCostM99Result = useMemo(() => {
    const flow = normalizeNumber(energyFlow);
    const deltaT = normalizeNumber(energyDeltaT);
    const hours = normalizeNumber(energyHoursM99);
    const price = normalizeNumber(energyPriceM99);
    if (flow === null || deltaT === null || hours === null || price === null) return '';
    const powerKw = 1.163 * flow * deltaT;
    const energyKwh = powerKw * hours;
    const cost = energyKwh * price;
    return `P=${numberFormatter.format(powerKw)} kW · E=${numberFormatter.format(energyKwh)} kWh · ${numberFormatter.format(cost)} €`;
  }, [energyDeltaT, energyFlow, energyHoursM99, energyPriceM99]);

  const roomEnergyResult = useMemo(() => {
    const volume = roomGeometryMode === 'volume'
      ? normalizeNumber(roomVolume)
      : ((normalizeNumber(roomArea) ?? 0) * (normalizeNumber(roomHeight) ?? 0));
    const deltaT = normalizeNumber(roomDeltaT);
    const hours = normalizeNumber(roomHoursM99);
    if (volume === null || !Number.isFinite(volume) || volume <= 0 || deltaT === null || hours === null) return '';
    if (hours <= 0) return 'Zeit muss > 0 sein.';
    const energy = (volume * 1.2 * 1.005 * deltaT) / 3600;
    const power = energy / hours;
    return `E≈${numberFormatter.format(energy)} kWh · P≈${numberFormatter.format(power)} kW`;
  }, [roomArea, roomDeltaT, roomGeometryMode, roomHeight, roomHoursM99, roomVolume]);

  const roomHeatingResult = useMemo(() => {
    const deltaNum = normalizeNumber(roomHeatDeltaT);
    const uNum = normalizeNumber(roomHeatU);
    const areaNum = normalizeNumber(roomHeatArea);
    const nNum = normalizeNumber(roomHeatN);
    const volNum = normalizeNumber(roomHeatVolume);
    const reheatNum = normalizeNumber(roomHeatReheat) ?? 0;
    if (deltaNum === null || uNum === null || areaNum === null || nNum === null || volNum === null) return '';
    const transmission = uNum * areaNum * deltaNum;
    const ventilation = 0.33 * nNum * volNum * deltaNum;
    const total = transmission + ventilation + reheatNum;
    return `Phi=${numberFormatter.format(total)} W`;
  }, [roomHeatDeltaT, roomHeatU, roomHeatArea, roomHeatN, roomHeatVolume, roomHeatReheat]);

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (event: PointerEvent) => {
      const deltaX = event.clientX - resizing.startX;
      const deltaY = event.clientY - resizing.startY;
      const wDelta = Math.round(deltaX / resizing.colWidth);
      const hDelta = Math.round(deltaY / GRID_ROW_HEIGHT);
      const next = clampWidgetSize({ w: resizing.startW + wDelta, h: resizing.startH + hDelta });
      setWidgetSizes((prev) => {
        const current = prev[resizing.key] || normalizeWidgetSize(undefined, resizing.key);
        if (current.w === next.w && current.h === next.h) return prev;
        return { ...prev, [resizing.key]: next };
      });
      setWidgetsDirty(true);
    };
    const handleUp = () => setResizing(null);
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [resizing]);

  const widgetRegistry = useMemo(() => {
    const map = new Map<string, WidgetConfig>();
    [...BASE_WIDGETS, ...M98_WIDGETS, ...M99_WIDGETS, ...QUICK_WIDGETS].forEach((widget) => {
      map.set(widget.key, widget);
    });
    return map;
  }, []);

  const availableWidgets = useMemo(() => {
    const activeKeys = new Set(widgetOrder);
    return [...BASE_WIDGETS, ...M98_WIDGETS, ...M99_WIDGETS, ...QUICK_WIDGETS].filter((widget) => !activeKeys.has(widget.key));
  }, [widgetOrder]);

  const orderedWidgets = useMemo(() => {
    return widgetOrder
      .map((key) => widgetRegistry.get(key))
      .filter(Boolean) as WidgetConfig[];
  }, [widgetOrder, widgetRegistry]);

  const renderWidgetContent = (widget: WidgetConfig) => {
    switch (widget.type) {
      case 'updates':
        return (
          <Stack spacing={1}>
            {visibleUpdates.length === 0 ? (
              <Typography variant="body2">Keine Updates vorhanden.</Typography>
            ) : (
              visibleUpdates.slice(0, 5).map((u) => (
                <Box key={u.id}>
                  <Typography variant="subtitle2" fontWeight="bold">{u.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{u.body}</Typography>
                </Box>
              ))
            )}
            <Button variant="outlined" onClick={() => setUpdatesOpen(true)}>
              Alle Updates anzeigen
            </Button>
          </Stack>
        );
      case 'highscore':
        return (
          <Stack spacing={1.5}>
            {topEntries.length === 0 ? (
              <Typography variant="body2">Noch keine Highscores vorhanden.</Typography>
            ) : (
              topEntries.map((entry) => (
                <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontWeight={600}>{entry.rank}. {entry.alias}</Typography>
                  <Typography color="text.secondary">{entry.totalPoints} Pkt</Typography>
                </Box>
              ))
            )}
            <Box sx={{ borderTop: '1px solid #eee', pt: 1 }}>
              <Typography variant="body2" fontWeight={600}>Mein Stand</Typography>
              {myScore ? (
                <Typography variant="body2" color="text.secondary">
                  {myScore.totalPoints} Punkte (Rang {myScore.rank ?? 'N/A'})
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">Kein Score gefunden.</Typography>
              )}
              <Button variant="text" component={RouterLink} to="/units/m98_u3_highscore">
                Zur Highscore-Seite
              </Button>
            </Box>
          </Stack>
        );
      case 'globalNote':
        return (
          <Stack spacing={2}>
            {canManageDashboard && (
              <Button
                variant="contained"
                onClick={() => {
                  setDashboardData((prev) => ({ ...prev, globalNote: localGlobalNote }));
                  handleSaveNotes({ globalNote: localGlobalNote });
                }}
                disabled={savingNotes}
              >
                {savingNotes ? 'Speichere ...' : 'Global speichern'}
              </Button>
            )}
            <TextField
              multiline
              minRows={4}
              value={localGlobalNote}
              onChange={(e) => setLocalGlobalNote(e.target.value)}
              onBlur={(e) => setDashboardData((prev) => ({ ...prev, globalNote: e.target.value }))}
              fullWidth
              disabled={!canManageDashboard}
            />
            {!canManageDashboard && (
              <Typography variant="caption" color="text.secondary">
                Nur berechtigte Nutzer koennen diese Notiz bearbeiten.
              </Typography>
            )}
          </Stack>
        );
      case 'userNote':
        return (
          <Stack spacing={2}>
            <Button
              variant="contained"
              onClick={() => {
                setDashboardData((prev) => ({ ...prev, userNote: localUserNote }));
                handleSaveNotes({ userNote: localUserNote });
              }}
              disabled={savingNotes}
            >
              {savingNotes ? 'Speichere ...' : 'Meine Notiz speichern'}
            </Button>
            <TextField
              multiline
              minRows={4}
              value={localUserNote}
              onChange={(e) => setLocalUserNote(e.target.value)}
              onBlur={(e) => setDashboardData((prev) => ({ ...prev, userNote: e.target.value }))}
              fullWidth
            />
          </Stack>
        );
      case 'faq':
        return (
          <Stack spacing={1}>
            {faqEntries.length === 0 ? (
              <Typography variant="body2">Keine FAQ-Eintraege vorhanden.</Typography>
            ) : (
              faqEntries.map((entry) => (
                <Typography key={entry.id} variant="body2">- {entry.question}</Typography>
              ))
            )}
            <Button variant="text" component={RouterLink} to="/units/m98_u1_f_a_q">
              Alle FAQ anzeigen
            </Button>
          </Stack>
        );
      case 'docs':
        return (
          <Stack spacing={1}>
            {!canViewDocuments ? (
              <Typography variant="body2">Keine Berechtigung fuer Dokumente.</Typography>
            ) : (
              <>
                {documents.length === 0 ? (
                  <Typography variant="body2">Keine Dokumente vorhanden.</Typography>
                ) : (
                  documents.slice(0, 3).map((doc) => (
                    <Typography key={doc.id} variant="body2">- {doc.title || doc.filename}</Typography>
                  ))
                )}
                <Button variant="text" component={RouterLink} to="/units/m98_u5_dokumente">
                  Alle Dokumente anzeigen
                </Button>
              </>
            )}
          </Stack>
        );
      case 'profile':
        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={profileImage || undefined} sx={{ width: 72, height: 72, bgcolor: 'grey.200' }}>
                {user?.alias?.[0] || user?.email?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>{user?.alias || user?.email}</Typography>
                <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
              </Box>
            </Stack>
            <input
              ref={profileImageInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleProfileImageChange}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => profileImageInputRef.current?.click()}
              >
                Bild auswaehlen
              </Button>
              <Button
                variant="contained"
                onClick={handleProfileImageUpload}
                disabled={profileImageUploading || !profileImageFile}
              >
                {profileImageUploading ? 'Lade hoch...' : 'Profilbild speichern'}
              </Button>
            </Stack>
            {profileImageMessage && (
              <Alert severity={profileImageMessage.includes('fehl') ? 'error' : 'success'}>
                {profileImageMessage}
              </Alert>
            )}
            <Button variant="text" component={RouterLink} to="/profile">
              Zum Profil
            </Button>
          </Stack>
        );
      case 'index':
        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label="Start" component={RouterLink} to="/" clickable />
              <Chip label="Profil" component={RouterLink} to="/profile" clickable />
              <Chip label="Dokumente" component={RouterLink} to="/units/m98_u5_dokumente" clickable />
              <Chip label="Quizzes" component={RouterLink} to="/quizzes" clickable />
              <Chip label="FAQ" component={RouterLink} to="/faq" clickable />
              <Chip label="Videos" component={RouterLink} to="/videos" clickable />
              <Chip label="Highscore" component={RouterLink} to="/units/m98_u3_highscore" clickable />
              {canAccessAdminPanel && <Chip label="Admin" component={RouterLink} to="/admin" clickable />}
              {canManageUpdates && <Chip label="Updates" component={RouterLink} to="/admin/updates" clickable />}
              {canManageThemeSettings && <Chip label="Design" component={RouterLink} to="/admin/theme" clickable />}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {MODULE_LINKS.map((mod) => (
                <Chip key={mod.id} label={mod.label} component={RouterLink} to={`/modules/${mod.id}`} clickable />
              ))}
            </Stack>
          </Stack>
        );
      default:
        if (widget.key === 'm99_u1_dipschalter') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Adresse (1-255)"
                  value={dipAddressInput}
                  onChange={(e) => setDipAddressInput(e.target.value)}
                  size="small"
                />
                <Button variant="outlined" onClick={handleApplyDipAddress}>
                  Uebernehmen
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Adresse: {dipAddressValue}
                </Typography>
              </Stack>
              {dipError && <Alert severity="error">{dipError}</Alert>}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                {DIP_VALUES.map((value, index) => (
                  <Stack key={value} alignItems="center" spacing={0.5}>
                    <DipSwitchSVG index={index} isOn={dipStates[index]} onToggle={() => handleDipToggle(index)} />
                    <Typography variant="caption" color="text.secondary">{value}</Typography>
                  </Stack>
                ))}
              </Box>
              <Button variant="text" component={RouterLink} to="/units/m99_u1_dipschalter">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u2_taupunkt') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Temp. (°C)"
                  value={tauTemp}
                  onChange={(e) => setTauTemp(e.target.value)}
                  size="small"
                />
                <TextField
                  label="rF (%)"
                  value={tauRh}
                  onChange={(e) => setTauRh(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {dewPointResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u2_taupunkt">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u3_volumenstrom') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="vol-shape">Kanal</InputLabel>
                  <Select
                    labelId="vol-shape"
                    label="Kanal"
                    value={volShape}
                    onChange={(e) => setVolShape(e.target.value as 'round' | 'rect')}
                  >
                    <MenuItem value="round">Rund</MenuItem>
                    <MenuItem value="rect">Rechteck</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="v (m/s)"
                  value={volSpeed}
                  onChange={(e) => setVolSpeed(e.target.value)}
                  size="small"
                />
                {volShape === 'round' ? (
                  <TextField
                    label="d (mm)"
                    value={volDiameter}
                    onChange={(e) => setVolDiameter(e.target.value)}
                    size="small"
                  />
                ) : (
                  <>
                    <TextField
                      label="a (mm)"
                      value={volWidth}
                      onChange={(e) => setVolWidth(e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="b (mm)"
                      value={volHeight}
                      onChange={(e) => setVolHeight(e.target.value)}
                      size="small"
                    />
                  </>
                )}
                <TextField
                  label="k-Faktor"
                  value={volKFactor}
                  onChange={(e) => setVolKFactor(e.target.value)}
                  size="small"
                  helperText="Optional (0.8-0.95)"
                  sx={{ minWidth: 140 }}
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {volumeFlowResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u3_volumenstrom">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u4_temperaturen') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="temp-sensor-type">Fuehler</InputLabel>
                  <Select
                    labelId="temp-sensor-type"
                    label="Fuehler"
                    value={tempSensorType}
                    onChange={(e) => setTempSensorType(e.target.value)}
                  >
                    {TEMPERATURE_SENSOR_MODELS.map((model) => (
                      <MenuItem key={model.id} value={model.id}>{model.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="temp-sensor-mode">Modus</InputLabel>
                  <Select
                    labelId="temp-sensor-mode"
                    label="Modus"
                    value={tempSensorDirection}
                    onChange={(e) => setTempSensorDirection(e.target.value as 'temp_to_res' | 'res_to_temp')}
                  >
                    <MenuItem value="temp_to_res">Temp → Widerstand</MenuItem>
                    <MenuItem value="res_to_temp">Widerstand → Temp</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={tempSensorDirection === 'temp_to_res' ? 'Temperatur (°C)' : 'Widerstand (Ω)'}
                  value={tempSensorDirection === 'temp_to_res' ? tempSensorValue : tempSensorResistanceValue}
                  onChange={(e) => {
                    if (tempSensorDirection === 'temp_to_res') {
                      setTempSensorValue(e.target.value);
                    } else {
                      setTempSensorResistanceValue(e.target.value);
                    }
                  }}
                  size="small"
                />
              </Stack>
              {selectedTempSensorModel && (
                <Typography variant="caption" color="text.secondary">
                  {tempSensorDirection === 'temp_to_res'
                    ? `Bereich: ${selectedTempSensorModel.minTempC} bis ${selectedTempSensorModel.maxTempC} °C`
                    : tempSensorResistanceRange
                      ? `Bereich: ${formatResistanceValue(tempSensorResistanceRange.minResistanceOhm)} bis ${formatResistanceValue(tempSensorResistanceRange.maxResistanceOhm)}`
                      : 'Bereich nicht verfuegbar.'}
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700}>
                {temperatureSensorQuickResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u4_temperaturen">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u5_kabelberechnung') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="cable-system">System</InputLabel>
                  <Select
                    labelId="cable-system"
                    label="System"
                    value={cableSystem}
                    onChange={(e) => setCableSystem(e.target.value as 'DC' | 'AC1' | 'AC3')}
                  >
                    <MenuItem value="DC">DC</MenuItem>
                    <MenuItem value="AC1">AC 1-ph</MenuItem>
                    <MenuItem value="AC3">AC 3-ph</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="P (kW)"
                  value={cablePower}
                  onChange={(e) => setCablePower(e.target.value)}
                  size="small"
                />
                <TextField
                  label="U (V)"
                  value={cableVoltage}
                  onChange={(e) => setCableVoltage(e.target.value)}
                  size="small"
                />
                {cableSystem !== 'DC' && (
                  <TextField
                    label="cos φ"
                    value={cableCosPhi}
                    onChange={(e) => setCableCosPhi(e.target.value)}
                    size="small"
                  />
                )}
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {cableQuickResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u5_kabelberechnung">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u6_leistungsberechnung') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="power-system">System</InputLabel>
                  <Select
                    labelId="power-system"
                    label="System"
                    value={powerSystem}
                    onChange={(e) => setPowerSystem(e.target.value as 'DC' | 'AC1' | 'AC3')}
                  >
                    <MenuItem value="DC">DC</MenuItem>
                    <MenuItem value="AC1">AC 1-ph</MenuItem>
                    <MenuItem value="AC3">AC 3-ph</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="U (V)"
                  value={powerVoltage}
                  onChange={(e) => setPowerVoltage(e.target.value)}
                  size="small"
                />
                <TextField
                  label="I (A)"
                  value={powerCurrent}
                  onChange={(e) => setPowerCurrent(e.target.value)}
                  size="small"
                />
                {powerSystem !== 'DC' && (
                  <TextField
                    label="cos φ"
                    value={powerCosPhi}
                    onChange={(e) => setPowerCosPhi(e.target.value)}
                    size="small"
                  />
                )}
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {quickPowerResult || powerQuickResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u6_leistungsberechnung">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u7_widerstand') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="U (V)"
                  value={resVoltage}
                  onChange={(e) => setResVoltage(e.target.value)}
                  size="small"
                />
                <TextField
                  label="I (A)"
                  value={resCurrent}
                  onChange={(e) => setResCurrent(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {resistanceQuickResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u7_widerstand">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u8_skalierer') {
          const x1 = normalizeNumber(scaleX1);
          const x2 = normalizeNumber(scaleX2);
          const y1 = normalizeNumber(scaleY1);
          const y2 = normalizeNumber(scaleY2);
          const points: string[] = [];
          if (x1 !== null && x2 !== null && y1 !== null && y2 !== null && x2 !== x1) {
            const xMin = Math.min(x1, x2);
            const xMax = Math.max(x1, x2);
            for (let i = 0; i <= 30; i += 1) {
              const x = xMin + ((xMax - xMin) * i) / 30;
              let y = y1 + ((x - x1) * (y2 - y1)) / (x2 - x1);
              if (scaleCurveModeWidget === 'parabola') {
                const a = normalizeNumber(scaleParAWidget) ?? 1;
                const h = normalizeNumber(scaleParHWidget) ?? 0;
                const k = normalizeNumber(scaleParKWidget) ?? 0;
                y = a * (x - h) * (x - h) + k;
              }
              if (scaleCurveModeWidget === 'sine') {
                const amp = normalizeNumber(scaleSinAmpWidget) ?? 30;
                const offset = normalizeNumber(scaleSinOffsetWidget) ?? 50;
                const period = normalizeNumber(scaleSinPeriodWidget) ?? 10;
                const phase = normalizeNumber(scaleSinPhaseWidget) ?? 0;
                if (period !== 0) {
                  y = offset + amp * Math.sin((2 * Math.PI * (x - phase)) / period);
                }
              }
              const sx = 6 + (i / 30) * 168;
              const sy = 44 - Math.max(0, Math.min(1, (y - Math.min(y1, y2)) / ((Math.max(y1, y2) - Math.min(y1, y2)) || 1))) * 36;
              points.push(`${i === 0 ? 'M' : 'L'} ${sx} ${sy}`);
            }
          }
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="scale-curve-widget">Kurve</InputLabel>
                  <Select
                    labelId="scale-curve-widget"
                    label="Kurve"
                    value={scaleCurveModeWidget}
                    onChange={(e) => setScaleCurveModeWidget(e.target.value as 'linear' | 'parabola' | 'sine')}
                  >
                    <MenuItem value="linear">Linear</MenuItem>
                    <MenuItem value="parabola">Parabel</MenuItem>
                    <MenuItem value="sine">Sinus</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel id="scale-direction-widget">Richtung</InputLabel>
                  <Select
                    labelId="scale-direction-widget"
                    label="Richtung"
                    value={scaleDirection}
                    onChange={(e) => setScaleDirection(e.target.value as 'forward' | 'reverse')}
                  >
                    <MenuItem value="forward">X → Y</MenuItem>
                    <MenuItem value="reverse">Y → X</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="x1" value={scaleX1} onChange={(e) => setScaleX1(e.target.value)} size="small" />
                <TextField label="x2" value={scaleX2} onChange={(e) => setScaleX2(e.target.value)} size="small" />
                <TextField label="y1" value={scaleY1} onChange={(e) => setScaleY1(e.target.value)} size="small" />
                <TextField label="y2" value={scaleY2} onChange={(e) => setScaleY2(e.target.value)} size="small" />
                <TextField label="Eingang" value={scaleInput} onChange={(e) => setScaleInput(e.target.value)} size="small" />
              </Stack>
              <Slider size="small" value={scaleSliderWidget} min={0} max={100} onChange={(_, v) => setScaleSliderWidget(v as number)} />
              <Box component="svg" viewBox="0 0 180 50" sx={{ width: 180, height: 50 }}>
                <line x1="6" y1="44" x2="174" y2="44" stroke="rgba(120,120,120,0.7)" strokeWidth="1" />
                <line x1="6" y1="8" x2="6" y2="44" stroke="rgba(120,120,120,0.7)" strokeWidth="1" />
                {points.length > 0 && <path d={points.join(' ')} fill="none" stroke={tokens.colors.primary} strokeWidth="2" />}
              </Box>
              <Typography variant="body2" fontWeight={700}>{scaleResult || 'Bitte Werte eingeben.'}</Typography>
              <Typography variant="caption" color="text.secondary">Formel: y = y1 + (x-x1)*(y2-y1)/(x2-x1)</Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u8_skalierer">Vollansicht</Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u9_farbcodex') {
          const palette = ['#B2140F', '#0D3559', '#2C5545', '#F7BA0B', '#293133', '#CBD0CC', '#F1F0EA'];
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Hex" value={colorHex} onChange={(e) => setColorHex(e.target.value)} size="small" />
                <TextField label="R" value={colorR} onChange={(e) => setColorR(e.target.value)} size="small" />
                <TextField label="G" value={colorG} onChange={(e) => setColorG(e.target.value)} size="small" />
                <TextField label="B" value={colorB} onChange={(e) => setColorB(e.target.value)} size="small" />
                <Box
                  component="input"
                  type="color"
                  value={colorPreviewHex}
                  onChange={(e) => setColorHex(e.target.value)}
                  sx={{ width: 56, height: 40, border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, background: 'transparent' }}
                />
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1, border: '1px solid rgba(0,0,0,0.2)', background: colorPreviewHex }} />
                <Typography variant="body2" fontWeight={700}>{colorCodexResult || 'Bitte Werte eingeben.'}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.8, maxWidth: 280 }}>
                {palette.map((hex) => (
                  <Button key={hex} variant="outlined" sx={{ minWidth: 0, p: 0.5, borderColor: 'transparent' }} onClick={() => setColorHex(hex)}>
                    <Box sx={{ width: '100%', height: 14, borderRadius: 0.8, border: '1px solid rgba(0,0,0,0.2)', background: hex }} />
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">Frei waehlen per Picker, dann HEX/RGB feinjustieren.</Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u9_farbcodex">Vollansicht</Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u10_energiekostenrechner') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Durchfluss (m³/h)" value={energyFlow} onChange={(e) => setEnergyFlow(e.target.value)} size="small" />
                <TextField label="Delta T (K)" value={energyDeltaT} onChange={(e) => setEnergyDeltaT(e.target.value)} size="small" />
                <TextField label="h" value={energyHoursM99} onChange={(e) => setEnergyHoursM99(e.target.value)} size="small" />
                <TextField label="€/kWh" value={energyPriceM99} onChange={(e) => setEnergyPriceM99(e.target.value)} size="small" />
              </Stack>
              <Typography variant="body2" fontWeight={700}>{energyCostM99Result || 'Bitte Werte eingeben.'}</Typography>
              <Typography variant="caption" color="text.secondary">Thermisch: P=1,163*Vdot*ΔT, E=P*h, Kosten=E*Preis.</Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u10_energiekostenrechner">Vollansicht</Button>
            </Stack>
          );
        }
        if (widget.key === 'm99_u11_energierechner') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="room-widget-mode">Rechner</InputLabel>
                  <Select
                    labelId="room-widget-mode"
                    label="Rechner"
                    value={roomWidgetMode}
                    onChange={(e) => setRoomWidgetMode(e.target.value as 'energy' | 'heating')}
                  >
                    <MenuItem value="energy">Energie</MenuItem>
                    <MenuItem value="heating">Heizlast DIN</MenuItem>
                  </Select>
                </FormControl>
                {roomWidgetMode === 'energy' ? (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="room-geo-mode-widget">Raumangabe</InputLabel>
                    <Select
                      labelId="room-geo-mode-widget"
                      label="Raumangabe"
                      value={roomGeometryMode}
                      onChange={(e) => setRoomGeometryMode(e.target.value as 'volume' | 'area_height')}
                    >
                      <MenuItem value="area_height">Flaeche + Hoehe</MenuItem>
                      <MenuItem value="volume">Volumen</MenuItem>
                    </Select>
                  </FormControl>
                ) : null}
                {roomWidgetMode === 'energy' ? (
                  roomGeometryMode === 'volume' ? (
                    <TextField label="Volumen (m³)" value={roomVolume} onChange={(e) => setRoomVolume(e.target.value)} size="small" />
                  ) : (
                    <>
                      <TextField label="Flaeche (m²)" value={roomArea} onChange={(e) => setRoomArea(e.target.value)} size="small" />
                      <TextField label="Hoehe (m)" value={roomHeight} onChange={(e) => setRoomHeight(e.target.value)} size="small" />
                    </>
                  )
                ) : (
                  <>
                    <TextField label="U (W/m²K)" value={roomHeatU} onChange={(e) => setRoomHeatU(e.target.value)} size="small" />
                    <TextField label="A (m²)" value={roomHeatArea} onChange={(e) => setRoomHeatArea(e.target.value)} size="small" />
                  </>
                )}
                {roomWidgetMode === 'energy' ? (
                  <>
                    <TextField label="Delta T (K)" value={roomDeltaT} onChange={(e) => setRoomDeltaT(e.target.value)} size="small" />
                    <TextField label="Zeit (h)" value={roomHoursM99} onChange={(e) => setRoomHoursM99(e.target.value)} size="small" />
                  </>
                ) : (
                  <>
                    <TextField label="n (1/h)" value={roomHeatN} onChange={(e) => setRoomHeatN(e.target.value)} size="small" />
                    <TextField label="V (m³)" value={roomHeatVolume} onChange={(e) => setRoomHeatVolume(e.target.value)} size="small" />
                    <TextField label="Delta T (K)" value={roomHeatDeltaT} onChange={(e) => setRoomHeatDeltaT(e.target.value)} size="small" />
                    <TextField label="Phi_A (W)" value={roomHeatReheat} onChange={(e) => setRoomHeatReheat(e.target.value)} size="small" />
                  </>
                )}
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {roomWidgetMode === 'energy'
                  ? (roomEnergyResult || 'Bitte Werte eingeben.')
                  : (roomHeatingResult || 'Bitte Werte eingeben.')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {roomWidgetMode === 'energy'
                  ? 'Luftmodell: E=(V*1,2*1,005*ΔT)/3600, mit V=A*h oder Direkteingabe.'
                  : 'DIN 12831: Phi_T=U*A*ΔT, Phi_V=0.33*n*V*ΔT, Phi_H=Summe + Phi_A.'}
              </Typography>
              <Button variant="text" component={RouterLink} to="/units/m99_u11_energierechner">Vollansicht</Button>
            </Stack>
          );
        }
        if (widget.key === 'm98_u2_vorschlaege') {
          return (
            <Stack spacing={2}>
              {suggestionMessage && <Alert severity="success">{suggestionMessage}</Alert>}
              {suggestionError && <Alert severity="error">{suggestionError}</Alert>}
              <input
                ref={suggestionFileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleSuggestionFileChange}
              />
              <Button variant="outlined" onClick={() => suggestionFileRef.current?.click()}>
                {suggestionFile ? 'Anderes Bild waehlen' : 'Bild auswaehlen'}
              </Button>
              {suggestionPreview && (
                <Box
                  component="img"
                  src={suggestionPreview}
                  alt="Vorschau"
                  sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}
                />
              )}
              <TextField
                label="Kommentar (optional)"
                value={suggestionComment}
                onChange={(e) => setSuggestionComment(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <Button variant="contained" onClick={handleSuggestionUpload} disabled={suggestionSending}>
                {suggestionSending ? 'Wird gesendet…' : 'Vorschlag senden'}
              </Button>
              <Button variant="text" component={RouterLink} to="/units/m98_u2_vorschlaege">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'm98_u4_telefonliste') {
          const filtered = contacts
            .filter((entry) => !contactsQuery.trim() || entry.name.toLowerCase().includes(contactsQuery.trim().toLowerCase()))
            .slice(0, 5);
          return (
            <Stack spacing={2}>
              <TextField
                label="Suche"
                value={contactsQuery}
                onChange={(e) => setContactsQuery(e.target.value)}
                size="small"
              />
              {contactsLoading ? (
                <Typography variant="body2">Lade Kontakte ...</Typography>
              ) : filtered.length === 0 ? (
                <Typography variant="body2">Keine Kontakte gefunden.</Typography>
              ) : (
                <Stack spacing={1}>
                  {filtered.map((entry) => (
                    <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                      <Avatar src={entry.image || DEFAULT_CONTACT_PLACEHOLDER} sx={{ width: 32, height: 32 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{entry.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.phone || entry.email || 'Keine Kontaktdaten'}
                        </Typography>
                      </Box>
                      {entry.phone && (
                        <Button size="small" component="a" href={`tel:${entry.phone.replace(/[^0-9+]/g, '')}`}>Anruf</Button>
                      )}
                      {entry.email && (
                        <Button size="small" component="a" href={`mailto:${entry.email}`}>Mail</Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
              <Button variant="text" component={RouterLink} to="/units/m98_u4_telefonliste">
                Vollansicht
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'quick_units') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="unit-type">Typ</InputLabel>
                  <Select
                    labelId="unit-type"
                    label="Typ"
                    value={unitType}
                    onChange={(e) => setUnitType(e.target.value as 'power' | 'temperature' | 'flow')}
                  >
                    <MenuItem value="power">Leistung</MenuItem>
                    <MenuItem value="temperature">Temperatur</MenuItem>
                    <MenuItem value="flow">Volumenstrom</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Wert"
                  value={unitValue}
                  onChange={(e) => setUnitValue(e.target.value)}
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="unit-input">Einheit</InputLabel>
                  <Select
                    labelId="unit-input"
                    label="Einheit"
                    value={unitInput}
                    onChange={(e) => setUnitInput(e.target.value as typeof unitInput)}
                  >
                    {unitType === 'power' && (
                      <>
                        <MenuItem value="W">W</MenuItem>
                        <MenuItem value="kW">kW</MenuItem>
                      </>
                    )}
                    {unitType === 'temperature' && (
                      <>
                        <MenuItem value="C">°C</MenuItem>
                        <MenuItem value="K">K</MenuItem>
                      </>
                    )}
                    {unitType === 'flow' && (
                      <>
                        <MenuItem value="m3h">m³/h</MenuItem>
                        <MenuItem value="ls">l/s</MenuItem>
                      </>
                    )}
                  </Select>
                </FormControl>
              </Stack>
              {unitConversionResult.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Bitte Wert eingeben.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {unitConversionResult.map((line) => (
                    <Typography key={line} variant="body2" fontWeight={700}>{line}</Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          );
        }
        if (widget.key === 'quick_ohm') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="ohm-target">Berechne</InputLabel>
                  <Select
                    labelId="ohm-target"
                    label="Berechne"
                    value={ohmTarget}
                    onChange={(e) => setOhmTarget(e.target.value as 'U' | 'I' | 'R')}
                  >
                    <MenuItem value="U">Spannung (U)</MenuItem>
                    <MenuItem value="I">Strom (I)</MenuItem>
                    <MenuItem value="R">Widerstand (R)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="U (V)"
                  value={ohmU}
                  onChange={(e) => setOhmU(e.target.value)}
                  size="small"
                  disabled={ohmTarget === 'U'}
                />
                <TextField
                  label="I (A)"
                  value={ohmI}
                  onChange={(e) => setOhmI(e.target.value)}
                  size="small"
                  disabled={ohmTarget === 'I'}
                />
                <TextField
                  label="R (Ω)"
                  value={ohmR}
                  onChange={(e) => setOhmR(e.target.value)}
                  size="small"
                  disabled={ohmTarget === 'R'}
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {ohmResult || 'Bitte 2 Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_pressure') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Wert"
                  value={pressureValue}
                  onChange={(e) => setPressureValue(e.target.value)}
                  size="small"
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="pressure-unit">Einheit</InputLabel>
                  <Select
                    labelId="pressure-unit"
                    label="Einheit"
                    value={pressureUnit}
                    onChange={(e) => setPressureUnit(e.target.value as 'Pa' | 'kPa' | 'mbar')}
                  >
                    <MenuItem value="Pa">Pa</MenuItem>
                    <MenuItem value="kPa">kPa</MenuItem>
                    <MenuItem value="mbar">mbar</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {pressureResult.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Bitte Wert eingeben.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {pressureResult.map((line) => (
                    <Typography key={line} variant="body2" fontWeight={700}>{line}</Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          );
        }
        if (widget.key === 'quick_power') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="quick-power-system">System</InputLabel>
                  <Select
                    labelId="quick-power-system"
                    label="System"
                    value={powerSystem}
                    onChange={(e) => setPowerSystem(e.target.value as 'DC' | 'AC1' | 'AC3')}
                  >
                    <MenuItem value="DC">DC</MenuItem>
                    <MenuItem value="AC1">AC 1-ph</MenuItem>
                    <MenuItem value="AC3">AC 3-ph</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="U (V)"
                  value={powerVoltage}
                  onChange={(e) => setPowerVoltage(e.target.value)}
                  size="small"
                />
                <TextField
                  label="I (A)"
                  value={powerCurrent}
                  onChange={(e) => setPowerCurrent(e.target.value)}
                  size="small"
                />
                {powerSystem !== 'DC' && (
                  <TextField
                    label="cos φ"
                    value={powerCosPhi}
                    onChange={(e) => setPowerCosPhi(e.target.value)}
                    size="small"
                  />
                )}
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {quickPowerResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_volume') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="quick-vol-shape">Kanal</InputLabel>
                  <Select
                    labelId="quick-vol-shape"
                    label="Kanal"
                    value={volShape}
                    onChange={(e) => setVolShape(e.target.value as 'round' | 'rect')}
                  >
                    <MenuItem value="round">Rund</MenuItem>
                    <MenuItem value="rect">Rechteck</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="v (m/s)"
                  value={volSpeed}
                  onChange={(e) => setVolSpeed(e.target.value)}
                  size="small"
                />
                {volShape === 'round' ? (
                  <TextField
                    label="d (mm)"
                    value={volDiameter}
                    onChange={(e) => setVolDiameter(e.target.value)}
                    size="small"
                  />
                ) : (
                  <>
                    <TextField
                      label="a (mm)"
                      value={volWidth}
                      onChange={(e) => setVolWidth(e.target.value)}
                      size="small"
                    />
                    <TextField
                      label="b (mm)"
                      value={volHeight}
                      onChange={(e) => setVolHeight(e.target.value)}
                      size="small"
                    />
                  </>
                )}
                <TextField
                  label="k-Faktor"
                  value={volKFactor}
                  onChange={(e) => setVolKFactor(e.target.value)}
                  size="small"
                  helperText="Optional (0.8-0.95)"
                  sx={{ minWidth: 140 }}
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {volumeFlowResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_taupunkt') {
          const tempValue = normalizeNumber(tauTemp);
          const rhValue = normalizeNumber(tauRh);
          const dew = dewPointValue;
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Temp. (°C)"
                  value={tauTemp}
                  onChange={(e) => setTauTemp(e.target.value)}
                  size="small"
                />
                <TextField
                  label="rF (%)"
                  value={tauRh}
                  onChange={(e) => setTauRh(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {dewPointResult || 'Bitte Werte eingeben.'}
              </Typography>
              <Box sx={{ width: '100%', height: 90, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <svg viewBox="0 0 240 90" width="100%" height="100%">
                  <rect width="240" height="90" fill="#f8fafc" />
                  <line x1="20" y1="70" x2="220" y2="70" stroke="#cbd5f5" strokeWidth="2" />
                  <text x="20" y="82" fontSize="10" fill="#64748b">0°C</text>
                  <text x="210" y="82" fontSize="10" fill="#64748b">40°C</text>
                  {tempValue !== null && (
                    <circle cx={20 + (Math.min(40, Math.max(0, tempValue)) / 40) * 200} cy={50} r={6} fill="#0ea5e9" />
                  )}
                  {dew !== null && (
                    <circle cx={20 + (Math.min(40, Math.max(0, dew)) / 40) * 200} cy={30} r={6} fill="#10b981" />
                  )}
                  <text x="20" y="18" fontSize="10" fill="#0ea5e9">T</text>
                  <text x="20" y="38" fontSize="10" fill="#10b981">TP</text>
                  {rhValue !== null && (
                    <text x="180" y="18" fontSize="10" fill="#64748b">rF {numberFormatter.format(rhValue)}%</text>
                  )}
                </svg>
              </Box>
            </Stack>
          );
        }
        if (widget.key === 'quick_contact') {
          const filtered = contacts
            .filter((entry) => !quickContactsQuery.trim() || entry.name.toLowerCase().includes(quickContactsQuery.trim().toLowerCase()))
            .slice(0, 5);
          return (
            <Stack spacing={2}>
              <TextField
                label="Kontakt suchen"
                value={quickContactsQuery}
                onChange={(e) => setQuickContactsQuery(e.target.value)}
                size="small"
              />
              {contactsLoading ? (
                <Typography variant="body2">Lade Kontakte ...</Typography>
              ) : filtered.length === 0 ? (
                <Typography variant="body2">Keine Treffer.</Typography>
              ) : (
                <Stack spacing={1}>
                  {filtered.map((entry) => (
                    <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                      <Avatar src={entry.image || DEFAULT_CONTACT_PLACEHOLDER} sx={{ width: 32, height: 32 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{entry.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.phone || entry.email || 'Keine Kontaktdaten'}
                        </Typography>
                      </Box>
                      {entry.phone && (
                        <Button size="small" component="a" href={`tel:${entry.phone.replace(/[^0-9+]/g, '')}`}>Anruf</Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
              <Button variant="text" component={RouterLink} to="/units/m98_u4_telefonliste">
                Telefonliste oeffnen
              </Button>
            </Stack>
          );
        }
        if (widget.key === 'quick_air_density') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="T (°C)"
                  value={airTemp}
                  onChange={(e) => setAirTemp(e.target.value)}
                  size="small"
                />
                <TextField
                  label="p (hPa)"
                  value={airPressure}
                  onChange={(e) => setAirPressure(e.target.value)}
                  size="small"
                />
                <TextField
                  label="rF (%)"
                  value={airRh}
                  onChange={(e) => setAirRh(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {airDensityResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_pipe_loss') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="v (m/s)"
                  value={pipeVelocity}
                  onChange={(e) => setPipeVelocity(e.target.value)}
                  size="small"
                />
                <TextField
                  label="D (mm)"
                  value={pipeDiameter}
                  onChange={(e) => setPipeDiameter(e.target.value)}
                  size="small"
                />
                <TextField
                  label="f"
                  value={pipeFriction}
                  onChange={(e) => setPipeFriction(e.target.value)}
                  size="small"
                />
                <TextField
                  label="ρ (kg/m³)"
                  value={pipeDensity}
                  onChange={(e) => setPipeDensity(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {pipeLossResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_energy_cost') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Leistung (kW)"
                  value={energyPower}
                  onChange={(e) => setEnergyPower(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Stunden"
                  value={energyHours}
                  onChange={(e) => setEnergyHours(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Preis €/kWh"
                  value={energyPrice}
                  onChange={(e) => setEnergyPrice(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {energyCostResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        if (widget.key === 'quick_fan_laws') {
          return (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Q1 (m³/h)"
                  value={fanQ1}
                  onChange={(e) => setFanQ1(e.target.value)}
                  size="small"
                />
                <TextField
                  label="Δp1 (Pa)"
                  value={fanDp1}
                  onChange={(e) => setFanDp1(e.target.value)}
                  size="small"
                />
                <TextField
                  label="P1 (W)"
                  value={fanP1}
                  onChange={(e) => setFanP1(e.target.value)}
                  size="small"
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="n1 (rpm)"
                  value={fanN1}
                  onChange={(e) => setFanN1(e.target.value)}
                  size="small"
                />
                <TextField
                  label="n2 (rpm)"
                  value={fanN2}
                  onChange={(e) => setFanN2(e.target.value)}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" fontWeight={700}>
                {fanLawsResult || 'Bitte Werte eingeben.'}
              </Typography>
            </Stack>
          );
        }
        return (
          <Stack spacing={1}>
            {widget.description && (
              <Typography variant="body2" color="text.secondary">{widget.description}</Typography>
            )}
            {widget.route && (
              <Button variant="outlined" component={RouterLink} to={widget.route}>
                Seite oeffnen
              </Button>
            )}
          </Stack>
        );
    }
  };

  const activeWidgetKeys = useMemo(() => widgetOrder.filter((key) => widgetEnabled[key] !== false), [widgetOrder, widgetEnabled]);

  const handleResizeStart = useCallback((key: string, size: WidgetSize, event: React.PointerEvent<HTMLDivElement>) => {
    if (!dashboardGridRef.current) return;
    event.stopPropagation();
    const rect = dashboardGridRef.current.getBoundingClientRect();
    const colWidth = (rect.width - (GRID_COLUMNS - 1) * widgetGap) / GRID_COLUMNS;
    setResizing({
      key,
      startX: event.clientX,
      startY: event.clientY,
      startW: size.w,
      startH: size.h,
      colWidth
    });
  }, [widgetGap]);

  const AvailableWidgetCard: React.FC<{ widget: WidgetConfig }> = ({ widget }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `available:${widget.key}`,
      data: { key: widget.key }
    });
    return (
      <Paper
        ref={setNodeRef}
        variant="outlined"
        sx={{ p: 1.5, opacity: isDragging ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Box {...listeners} {...attributes} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>{widget.title}</Typography>
          {widget.description && (
            <Typography variant="caption" color="text.secondary">{widget.description}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={() => handleAddWidget(widget.key)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  };

  return (
    <UnitPageShell
      title="Dashboard"
      showTTS={false}
      actions={
        <Stack direction="row" spacing={1}>
          {canPlayCarRace && (
            <Button color="inherit" startIcon={<SportsMotorsportsIcon />} onClick={() => setRaceOpen(true)}>
              Auto-Rennspiel
            </Button>
          )}
          <Button color="inherit" startIcon={<SettingsIcon />} onClick={() => setDrawerOpen(true)}>
            Dashboard bearbeiten
          </Button>
        </Stack>
      }
    >
      <Container maxWidth={false} disableGutters sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 }, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        {message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {visibleUpdates.length > 0 && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Neu: {visibleUpdates[0].title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {visibleUpdates[0].body}
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => setUpdatesOpen(true)}>
              Alle Updates
            </Button>
          </Paper>
        )}

        <Paper
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(120deg, #0D3559 0%, #B2140F 100%)',
            color: '#fff',
            cursor: 'pointer',
            overflow: 'hidden',
            position: 'relative'
          }}
          onClick={() => setUpdatesOpen(true)}
        >
          <Box
            sx={{
              display: 'flex',
              whiteSpace: 'nowrap',
              '@keyframes updates-marquee': {
                '0%': { transform: 'translateX(0%)' },
                '100%': { transform: 'translateX(-50%)' }
              },
              animation: 'updates-marquee 45s linear infinite'
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', pr: 4 }}>
              <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700, mr: 2 }}>
                🔔 Updates:
              </Typography>
              <Typography variant="body2" component="span">
                {updatesBannerText}
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', pr: 4 }}>
              <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700, mr: 2 }}>
                🔔 Updates:
              </Typography>
              <Typography variant="body2" component="span">
                {updatesBannerText}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <DashboardDropZone>
            {activeWidgetKeys.length === 0 ? (
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Dashboard ist leer. Oeffne den Editor und ziehe Widgets hinein.
                </Typography>
              </Paper>
            ) : (
              <SortableContext items={activeWidgetKeys} strategy={rectSortingStrategy}>
                <Box
                  ref={dashboardGridRef}
                  sx={{
                    display: 'grid',
                    gap: `${widgetGap}px`,
                    p: 1,
                    boxSizing: 'border-box',
                    gridTemplateColumns: { xs: 'repeat(12, 1fr)', sm: 'repeat(12, 1fr)', md: `repeat(${GRID_COLUMNS}, 1fr)` },
                    gridAutoRows: 'auto',
                    gridAutoFlow: 'row dense',
                    alignItems: 'start',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'visible'
                  }}
                >
                  {orderedWidgets
                    .filter((widget) => widgetEnabled[widget.key] !== false)
                    .map((widget) => (
                      <SortableWidgetCard
                        key={widget.key}
                        widget={widget}
                        dragDisabled={dragDisabled}
                        widgetSizes={widgetSizes}
                        widgetGap={widgetGap}
                        isSmallScreen={isSmallScreen}
                        onRemove={handleRemoveWidget}
                        onResizeStart={handleResizeStart}
                        renderWidgetContent={renderWidgetContent}
                        setDragDisabled={setDragDisabled}
                      />
                    ))}
                </Box>
              </SortableContext>
            )}
          </DashboardDropZone>
          <DragOverlay>
            <Paper sx={{ p: 1, borderRadius: 1 }}>
              <Typography variant="body2">Widget verschieben</Typography>
            </Paper>
          </DragOverlay>
        </DndContext>
      </Container>

      <Dialog open={updatesOpen} onClose={() => setUpdatesOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Letzte Updates</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {visibleUpdates.length === 0 ? (
              <Typography variant="body2">Keine Updates vorhanden.</Typography>
            ) : (
              visibleUpdates.map((u) => (
                <Box key={u.id}>
                  <Typography variant="subtitle1" fontWeight="bold">{u.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{u.body}</Typography>
                </Box>
              ))
            )}
            {canManageUpdates && (
              <Button variant="outlined" component={RouterLink} to="/admin/updates" onClick={() => setUpdatesOpen(false)}>
                Updates verwalten
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={raceOpen} onClose={() => setRaceOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle>Auto-Rennspiel</DialogTitle>
        <DialogContent dividers>
          <CarRaceGame />
        </DialogContent>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 380 }, p: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                Dashboard-Editor
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Button
              variant="contained"
              onClick={handleSaveWidgets}
              disabled={!widgetsDirty || savingWidgets}
            >
              {savingWidgets ? 'Speichere ...' : 'Layout speichern'}
            </Button>

            <Typography variant="body2" color="text.secondary">
              Widgets per Drag & Drop hinzufuegen oder entfernen. Reihenfolge und Groesse werden pro User gespeichert.
            </Typography>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Widget-Abstand
              </Typography>
              <Stack spacing={1}>
                <Slider
                  value={widgetGap}
                  onChange={(_, value) => {
                    const next = Array.isArray(value) ? value[0] : value;
                    const bounded = Math.max(8, Math.min(48, Number(next)));
                    setWidgetGap(bounded);
                    setWidgetsDirty(true);
                  }}
                  min={8}
                  max={48}
                  step={2}
                  valueLabelDisplay="auto"
                />
                <Typography variant="caption" color="text.secondary">
                  Aktuell: {widgetGap}px
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Aktive Widgets
              </Typography>
              <Stack spacing={1}>
                {activeWidgetKeys.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Noch keine Widgets aktiv.</Typography>
                ) : (
                  orderedWidgets
                    .filter((widget) => widgetEnabled[widget.key] !== false)
                    .map((widget) => (
                      <Paper key={widget.key} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" fontWeight={700}>{widget.title}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">Breite</Typography>
                            <Button size="small" variant="outlined" onClick={() => adjustWidgetSize(widget.key, -1, 0)}>-</Button>
                            <Typography variant="body2">
                              {(widgetSizes[widget.key] || normalizeWidgetSize(undefined, widget.key)).w}
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => adjustWidgetSize(widget.key, 1, 0)}>+</Button>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Hoehe</Typography>
                            <Button size="small" variant="outlined" onClick={() => adjustWidgetSize(widget.key, 0, -1)}>-</Button>
                            <Typography variant="body2">
                              {(widgetSizes[widget.key] || normalizeWidgetSize(undefined, widget.key)).h}
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => adjustWidgetSize(widget.key, 0, 1)}>+</Button>
                          </Stack>
                          <Button variant="outlined" color="error" onClick={() => handleRemoveWidget(widget.key)}>
                            Entfernen
                          </Button>
                        </Stack>
                      </Paper>
                    ))
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Verfuegbare Widgets
              </Typography>
              <Stack spacing={1}>
                {availableWidgets.map((widget) => (
                  <AvailableWidgetCard key={widget.key} widget={widget} />
                ))}
              </Stack>
            </Box>

            <Button
              variant="contained"
              onClick={handleSaveWidgets}
              disabled={!widgetsDirty || savingWidgets}
            >
              {savingWidgets ? 'Speichere ...' : 'Layout speichern'}
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </UnitPageShell>
  );
};

export default Start;
